<script setup lang="ts">
import {
  ArrowUpDown as ArrowUpDownIcon,
  Bike as BikeIcon,
  Check as CheckIcon,
  CircleCheck as CircleCheckIcon,
  ChevronLeft as ChevronLeftIcon,
  Download as DownloadIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Focus as FocusIcon,
  Gauge as GaugeIcon,
  ImagePlus as ImagePlusIcon,
  Info as InfoIcon,
  Loader as LoaderIcon,
  MapPin as MapPinIcon,
  MoreHorizontal as MoreHorizontalIcon,
  Navigation as NavigationIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  Plus as PlusIcon,
  RotateCcw as RestoreIcon,
  Route as RouteIcon,
  Search as SearchIcon,
  LogOut as LogOutIcon,
  Pencil as PencilIcon,
  Settings as SettingsIcon,
  Share2 as ShareIcon,
  SquarePlus as SquarePlusIcon,
  Trash2 as TrashIcon,
  Upload as UploadIcon,
  X as XIcon,
  FolderMinus as FolderMinusIcon,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'
import {
  ref,
  computed,
  inject,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  type Component,
  type Ref,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import OfflineDownloadDialog from '@/components/dialogs/OfflineDownloadDialog.vue'
import CreateTourModal from '@/components/map/modals/CreateTourModal.vue'
import GpxDownloadOptionsModal from '@/components/map/modals/GpxDownloadOptionsModal.vue'
import ShareModal from '@/components/map/modals/ShareWorkspaceModal.vue'
import TourDeleteModal from '@/components/map/modals/TourDeleteModal.vue'
import UploadDialog from '@/components/map/modals/UploadDialog.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseIconButton from '@/components/ui/BaseIconButton.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseRemovableTag from '@/components/ui/BaseRemovableTag.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { useEventListener } from '@/composables/useEventListener'
import type { MapStylePreference } from '@/config/mapStyle'
import { DEFAULT_MAP_STYLE_PREFERENCE } from '@/config/mapStyle'
import { openAddToCollection, tourToAddToCollectionItem } from '@/services/addToCollectionService'
import { apiService } from '@/services/apiService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import {
  formatCollectionDistance,
  formatCollectionDuration,
  getDaysUntilDeletion,
} from '@/services/formattingService'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { notifyMobilePanelDragStart } from '@/services/mobilePanelGestureService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { requireOnlineModificationAccess } from '@/services/offlineModificationGuardService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import {
  canUnpinPOIFromPinned,
  setPinnedItemsBatch,
  unpinAllPinnedItems,
} from '@/services/pinnedService'
import { isTauri } from '@/services/platformService'
import { requestRouteModeLeave } from '@/services/routeModeLeaveGuardService'
import {
  FLOATING_SHEET_BOTTOM_OFFSET,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
  readSafeAreaInsetTopPx,
  useSafeAreaInsets,
} from '@/services/safeAreaService'
import { settingsService } from '@/services/settingsService'
import { syncService } from '@/services/syncService'
import { showError, showInfo, showSuccess } from '@/services/toastService'
import {
  shouldHideAscentForTour,
  shouldHideAscentForTourListItem,
  shouldHideAscentForTrack,
} from '@/services/tourTypeService'
import { TrackColorService } from '@/services/trackColorService'
import {
  calculateHaversineDistance,
  calculateTrackDistance,
  getTrackElevationGain,
  getTrackElevationLoss,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import { isTouchDevice } from '@/services/uiService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useMapStore } from '@/stores/mapStore'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { useOfflineStore } from '@/stores/offlineStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Photo } from '@/types/photo'
import type { Track, Tour, TourListItem } from '@/types/tour'
import type { Workspace, WorkspaceMember } from '@/types/workspace'
import { getLibraryToursToRender } from '@/utils/libraryMapVisibility'
import { resolveMobileBatchPermissionState } from '@/utils/mobileBatchActionPermissions'
import { getEffectiveTourName, getTourGlobalNameSubtitle } from '@/utils/tourName'
import { isDownloadableTourSourceFilename } from '@/utils/tourSource'
import { getBestTrackLineString } from '@/utils/trackGeometry'
import { getWorkspaceAuthorByline } from '@/utils/workspaceAuthorByline'
import { Z_INDEX } from '@/utils/zIndex'

import ActivityRouteCollection from './toolbar/ActivityRouteCollection.vue'
import CollectionRouteEmptyState from './toolbar/CollectionRouteEmptyState.vue'
import PhotoList from './toolbar/PhotoList.vue'
import PinnedEmptyState from './toolbar/PinnedEmptyState.vue'
import TourList from './toolbar/TourList.vue'
import WorkspacePOIList from './toolbar/WorkspacePOIList.vue'
import WorkspaceSearchPanel from './toolbar/WorkspaceSearchPanel.vue'

const activityRouteCollectionRef = ref<InstanceType<typeof ActivityRouteCollection> | null>(null)
const isCreateTourModalOpen = ref(false)

const emit = defineEmits<{
  'trigger-gpx-upload': []
  'trigger-photo-upload': []
  'trigger-mixed-upload': []
  'create-new-workspace': []
  'tour-click': [tourId: string]
  'view-photo': [photoId: string]
  'zoom-photo': [photoId: string]
  'zoom-poi': [poiId: string]
  'open-poi-detail': [poiId: string]
  'zoom-tour': [tourId: string]
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'select-tour': [tourId: string]
  'select-workspace': [workspaceId: string]
  'make-offline': [workspaceId: string]
  'create-tour': []
  'enter-track-edit': [tourId: string, trackClientId: string]
  'navigate-tour': [tourId: string]
  'open-route-profile': []
  'open-route-details': []
  'high-header-back': []
}>()

const props = withDefaults(
  defineProps<{
    selectedPoiId?: string | null
    selectedRouteTourId?: string | null
    canNavigateSelectedRoute?: boolean
    canShowSelectedRouteProfile?: boolean
    canShowSelectedRouteDetails?: boolean
    showHighHeaderBack?: boolean
    showHighHeaderPrimaryAction?: boolean
    highHeaderBackAriaLabel?: string
  }>(),
  {
    selectedPoiId: null,
    selectedRouteTourId: null,
    canNavigateSelectedRoute: false,
    canShowSelectedRouteProfile: false,
    canShowSelectedRouteDetails: false,
    showHighHeaderBack: false,
    showHighHeaderPrimaryAction: false,
    highHeaderBackAriaLabel: 'Back',
  },
)

const uiStore = useUiStore()
const libraryStore = useLibraryStore()
const workspaceStore = useWorkspaceStore()
const searchWorkspaceStore = useSearchWorkspaceStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const authStore = useAuthStore()
const mapStore = useMapStore()
const offlineStore = useOfflineStore()
const router = useRouter()
const route = useRoute()
const photoStore = usePhotoStore()
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const isTauriApp = isTauri()
const safeAreaInsets = useSafeAreaInsets()
const { photosSortedByDate } = storeToRefs(photoStore)
const hasPhotos = computed(() => (photosSortedByDate?.value?.length ?? 0) > 0)
const tours = computed(() => workspaceStore.tours ?? [])
const hasTours = computed(() => tours.value.length > 0)
const hasPOIs = computed(() => workspaceStore.allWorkspacePOIs.length > 0)
const libraryToursVisibleOnMap = computed(() =>
  getLibraryToursToRender({
    tours: libraryStore.tours,
    showOnlyUploadedBatch: libraryStore.showOnlyUploadedBatch,
    uploadedBatchIds: libraryStore.uploadedBatchIds,
    isTrackEditMode: uiStore.isTrackEditMode,
    selectedTourIds: libraryStore.selectedTourIds,
    editingTrackTourId: uiStore.editingTrackTourId,
    editingTrackClientId: uiStore.editingTrackClientId,
    showAllTours: uiStore.isAllActivitiesView,
    clientSideFilteredIds: libraryStore.clientSideFilteredIds,
    pinnedTourIds: libraryStore.pinnedTourIds,
  }),
)
const hasWorkspaceMapContent = computed(
  () => workspaceStore.calculateMapBounds() !== null || photoStore.calculateMapBounds() !== null,
)
const hasOverviewMapContent = computed(
  () => searchWorkspaceStore.getVisibleWorkspacesBounds() !== null,
)
const mobileHeaderIconButtonClass =
  'flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 transition-colors hover:cursor-pointer hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40'
const mobileHeaderPillButtonClass =
  'inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-white px-4 text-base font-medium text-gray-900 transition-colors hover:cursor-pointer hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40'
const isPinnedWorkspaceEmpty = computed(() => !hasTours.value && !hasPOIs.value && !hasPhotos.value)
const hasItemsToUnpin = computed(
  () =>
    hasTours.value ||
    workspaceStore.allWorkspacePOIs.some((poi) =>
      canUnpinPOIFromPinned(poi, workspaceStore.clientId),
    ),
)
const canUnpinAllPinnedItems = computed(() => hasItemsToUnpin.value && hasPermission('edit_tour'))
const isPinnedWorkspaceDetail = computed(
  () =>
    currentView.value === 'workspace-detail' &&
    (route.name === 'pinned' || (workspaceStore.isSystem && workspaceStore.systemKey === 'pinned')),
)
const isPinnedWorkspaceContentReady = computed(
  () =>
    !isPinnedWorkspaceDetail.value ||
    (workspaceStore._isLoading !== true &&
      workspaceStore.isSystem === true &&
      workspaceStore.systemKey === 'pinned' &&
      !!workspaceStore.clientId),
)
const isPinnedWorkspaceDisplayReady = isPinnedWorkspaceContentReady
const isAnyTourVisible = computed(() => tours.value.some((tour) => tour.visible))
const isLoadingTours = computed(() => workspaceStore?._isLoading || false)
const isLoadingPhotos = computed(() => photoStore?.isLoading || false)
const isContentLoading = computed(() => isLoadingTours.value || isLoadingPhotos.value)
const arePhotosVisible = computed(() => uiStore.arePhotosVisible)

const { isMobileBottomPanelExpanded, isDialogOpen } = storeToRefs(uiStore)

// Scroll container refs used for section navigation.
const scrollContainer = ref<HTMLElement | null>(null)
const photosHeaderRef = ref<HTMLElement | null>(null)
const photosSectionRef = ref<HTMLElement | null>(null)
const isPhotosHeaderVisible = ref(false)
const poiHeaderRef = ref<HTMLElement | null>(null)
const isPOIHeaderVisible = ref(false)

// Touch/mouse gesture state
const isDragging = ref(false)
const startY = ref(0)
const currentY = ref(0)
const startTime = ref(0)
const dragOffset = ref(0)
const dragStartHeight = ref(0)
const dragMode = ref<null | 'touch' | 'mouse'>(null)
const mouseDragged = ref(false)
const scrollDragCandidate = ref<{
  startX: number
  startY: number
  startTime: number
} | null>(null)
const scrollDragOwnsPanel = ref(false)
const scrollDragScroller = ref<HTMLElement | null>(null)
let previousScrollerOverscrollBehaviorY = ''
let suppressScrollContextMenuUntil = 0
type PanelHeightMode = 'collapsed' | 'expanded' | 'high'
const panelHeightMode = ref<PanelHeightMode>('collapsed')
/* Panel animation state */
const isAnimating = ref(false)
const isHighPanelHeader = computed(() => panelHeightMode.value === 'high' && !isAnimating.value)
const highPanelHeaderTitle = computed(() => {
  if (currentView.value === 'workspaces') return 'Collections'
  if (currentView.value === 'all-activities') return 'Library'
  if (isPinnedWorkspaceDetail.value) return 'Pinned'
  return workspaceStore.name?.trim() || 'Untitled Collection'
})
const workspaceAuthorByline = computed(() =>
  getWorkspaceAuthorByline({
    isOwner: workspaceStore.isOwner,
    ownerNames: workspaceStore.ownerNames,
    currentUserName: authStore.user?.name,
  }),
)

watch(isMobileBottomPanelExpanded, (expanded) => {
  const nextMode = expanded
    ? panelHeightMode.value === 'collapsed'
      ? 'expanded'
      : panelHeightMode.value
    : 'collapsed'
  if (nextMode !== panelHeightMode.value) animatePanelToMode(nextMode, false)
})

// Publish only the settled high snap. During the height transition, the map-owned floating chrome
// stays visible; ownership moves into this header after transitionend clears `isAnimating`.
watch(isHighPanelHeader, (high) => uiStore.setMobileBottomPanelHigh(high), { immediate: true })

// Root panel element to listen for transitionend
const panelRef = ref<HTMLElement | null>(null)
const renderedPanelHeight = ref<number | null>(null)
let panelHeightTrackingFrame: number | null = null
let scheduledPanelHeightPublishFrame: number | null = null

// Baseline map inset in map view: clears the floating tab bar (≈ bar height + offset).
const MOBILE_MAP_VIEW_INSET = 84

const publishMobileBottomPanelHeight = () => {
  // In map view the drawer is hidden (display:none → a zero-rect), so don't measure it;
  // publish a baseline that just clears the floating tab bar.
  if (uiStore.isMobileMapView) {
    uiStore.setMobileBottomPanelExpandedProgress(0)
    uiStore.setMobileBottomPanelHighProgress(0, MOBILE_MAP_VIEW_INSET)
    uiStore.setMobileBottomPanelHeight(MOBILE_MAP_VIEW_INSET)
    return
  }
  // Publish the distance from the viewport bottom to the panel top so the map's bottom
  // inset stays correct now that the panel floats above the tab bar (rather than sitting
  // at bottom: 0). Equivalent to the panel height when bottom: 0.
  const rect = panelRef.value?.getBoundingClientRect()
  // When this drawer is hidden (e.g. the Canvas sheet is open → an ancestor sets
  // display:none) the rect collapses to all-zeros. Measuring it would publish a bogus
  // full-viewport height and clobber whatever component currently owns the shared value
  // (the Canvas sheet), throwing the create FAB to a garbage position. Bail instead.
  if (rect && rect.width === 0 && rect.height === 0) return
  if (rect && rect.height > 0) renderedPanelHeight.value = rect.height
  const occupiedFromBottom =
    rect && typeof window !== 'undefined' ? window.innerHeight - rect.top : 0
  const bottomGap =
    rect && typeof window !== 'undefined' ? Math.max(0, window.innerHeight - rect.bottom) : 0
  uiStore.setMobileBottomPanelHighProgress(
    highTransitionProgress.value,
    Math.round(bottomGap + maxPanelHeight.value),
  )
  uiStore.setMobileBottomPanelExpandedProgress(expandedTransitionProgress.value)
  uiStore.setMobileBottomPanelHeight(
    occupiedFromBottom > 0 ? Math.round(occupiedFromBottom) : panelHeight.value,
  )
}

const cancelScheduledPanelHeightPublish = () => {
  if (scheduledPanelHeightPublishFrame !== null) {
    cancelAnimationFrame(scheduledPanelHeightPublishFrame)
    scheduledPanelHeightPublishFrame = null
  }
}

const scheduleMeasuredMobileBottomPanelHeightPublish = () => {
  cancelScheduledPanelHeightPublish()
  nextTick(() => {
    scheduledPanelHeightPublishFrame = requestAnimationFrame(() => {
      scheduledPanelHeightPublishFrame = null
      publishMobileBottomPanelHeight()
    })
  })
}

const stopPanelHeightTracking = () => {
  if (panelHeightTrackingFrame !== null) {
    cancelAnimationFrame(panelHeightTrackingFrame)
    panelHeightTrackingFrame = null
  }
}

const startPanelHeightTracking = () => {
  stopPanelHeightTracking()
  const tick = () => {
    publishMobileBottomPanelHeight()
    if (isDragging.value || isAnimating.value) {
      panelHeightTrackingFrame = requestAnimationFrame(tick)
      return
    }
    panelHeightTrackingFrame = null
  }
  panelHeightTrackingFrame = requestAnimationFrame(tick)
}

// Helper to reset animation flag when the height transition finishes
const resetAfterTransition = () => {
  // The @transitionend handler on the panel element (handleMobileToolbarTransitionEnd)
  // is the primary way isAnimating is reset. Only run a fallback here when the panel
  // ref is not yet available (e.g. during initial mount), so we don't cancel an
  // in-progress transition by removing the transition style too early.
  if (panelRef.value) return
  requestAnimationFrame(() => {
    isAnimating.value = false
    uiStore.markToolbarLayoutStable()
  })
}

function animatePanelToMode(nextMode: PanelHeightMode, syncExpandedState = true) {
  if (nextMode === panelHeightMode.value) {
    if (syncExpandedState) {
      if (nextMode === 'collapsed') uiStore.collapseMobileBottomPanel()
      else uiStore.expandMobileBottomPanel()
    }
    return
  }

  const visibleHeight = panelRef.value?.getBoundingClientRect().height
  if (visibleHeight && visibleHeight > 0) renderedPanelHeight.value = visibleHeight
  isAnimating.value = panelRef.value !== null
  panelHeightMode.value = nextMode

  if (syncExpandedState) {
    if (nextMode === 'collapsed') uiStore.collapseMobileBottomPanel()
    else uiStore.expandMobileBottomPanel()
  }

  if (isAnimating.value) startPanelHeightTracking()
  resetAfterTransition()
}

// Handle mobile toolbar transition completion
const handleMobileToolbarTransitionEnd = () => {
  publishMobileBottomPanelHeight()
  isAnimating.value = false
  uiStore.markToolbarLayoutStable()
}

// Resize handler reference for cleanup
const resizeHandler = () => {
  // Force re-computation of computed properties on resize
  if (!isMobileBottomPanelExpanded.value) {
    // Reset animation state on resize
    isAnimating.value = false
    isDragging.value = false
  }

  // Touch-specific handling for viewport changes
  if (isTouch) {
    // Let the browser finish its viewport adjustments without an arbitrary timeout:
    // wait for Vue to flush, then for the next animation frame(s)
    nextTick(() => {
      requestAnimationFrame(() => {
        if (isMobileBottomPanelExpanded.value) {
          // Refresh the panel height calculation
          uiStore.expandMobileBottomPanel()
        }
      })
    })
  }
}

// Component state
// Initialize from route params (same reasoning as WorkspaceToolbar.vue): workspaceStore.clientId is
// always a truthy UUID on init and can't distinguish "no workspace" from "workspace loaded".
// Using route params avoids a brief flash of the collections overview when hard-refreshing
// on a workspace detail URL, which would cause WorkspaceSearchPanel to mount unnecessarily.
const isPinnedRoute = () => route.name === 'pinned'
const hasActiveWorkspace =
  (typeof route.params.workspaceId === 'string' && !!route.params.workspaceId) || isPinnedRoute()
const currentView = ref<
  'workspaces' | 'workspace-detail' | 'all-activities' | 'all-activities-tour-detail'
>(hasActiveWorkspace ? 'workspace-detail' : 'workspaces')
const mobileTourDetailTour = ref<TourListItem | null>(null)
const canDeletePhotos = computed(() => hasPermission('delete_photo'))
const canSetCollectionCover = computed(
  () =>
    !isPinnedWorkspaceDetail.value &&
    workspaceStore.isSystem !== true &&
    hasPermission('edit_tour'),
)

const mobileBatchSurface = computed(() => {
  if (currentView.value === 'all-activities') return 'library'
  if (currentView.value === 'workspaces') return 'collections-overview'
  if (currentView.value === 'workspace-detail') {
    return isPinnedWorkspaceDetail.value ? 'pinned' : 'collection-detail'
  }
  return null
})

const mobilePanelSurfaceKey = computed(() => {
  if (route.name === 'pinned' || isPinnedWorkspaceDetail.value) return 'pinned'
  if (currentView.value === 'all-activities-tour-detail') {
    return `library-tour:${mobileTourDetailTour.value?.clientId ?? 'detail'}`
  }
  if (currentView.value === 'all-activities') return 'library'
  if (currentView.value === 'workspaces') return 'collections-overview'

  const routeWorkspaceId =
    typeof route.params.workspaceId === 'string' ? route.params.workspaceId : null
  return `collection:${routeWorkspaceId ?? workspaceStore.clientId}`
})

watch(mobilePanelSurfaceKey, (surface, previousSurface) => {
  if (!previousSurface || surface === previousSurface || panelHeightMode.value !== 'high') return

  animatePanelToMode('expanded')
})

const isMobileBatchMode = computed(
  () =>
    mobileBatchSelectionStore.isActive &&
    mobileBatchSurface.value !== null &&
    mobileBatchSelectionStore.activeSurface === mobileBatchSurface.value,
)

const selectedLibraryTours = computed(() =>
  libraryStore.tours.filter((tour) => mobileBatchSelectionStore.tours.has(tour.clientId)),
)
const selectedWorkspaceTours = computed(() =>
  workspaceStore.tours.filter((tour) => mobileBatchSelectionStore.tours.has(tour.clientId)),
)
const selectedWorkspacePOIs = computed(() =>
  workspaceStore.allWorkspacePOIs.filter((poi) => mobileBatchSelectionStore.pois.has(poi.clientId)),
)
const selectedPhotos = computed(() =>
  photosSortedByDate.value.filter((photo) => mobileBatchSelectionStore.photos.has(photo.clientId)),
)
const selectedOverviewCollections = computed(() =>
  searchWorkspaceStore.allWorkspaces.filter((workspace) =>
    mobileBatchSelectionStore.collections.has(workspace.clientId),
  ),
)
const isOverviewCollectionVisible = (workspaceId: string) =>
  searchWorkspaceStore.selectedWorkspaces.find((workspace) => workspace.clientId === workspaceId)
    ?.isVisible === true
const selectedBatchTours = computed(() =>
  mobileBatchSurface.value === 'library'
    ? selectedLibraryTours.value
    : selectedWorkspaceTours.value,
)
const mobileBatchSingleDeleteTour = ref<Tour | TourListItem | null>(null)
const isMobileBatchSingleDeleteOpen = computed(() => mobileBatchSingleDeleteTour.value !== null)
const isMobileBatchSingleDeleting = ref(false)
const includeCurrentCollectionInMobileBatchDelete = computed(
  () => mobileBatchSurface.value !== 'library',
)
const isSingleTourOnlyMobileBatchSelection = computed(
  () => selectedBatchTours.value.length === 1 && mobileBatchSelectionStore.selectedCount === 1,
)
const currentBatchSelectableIds = computed(() => {
  if (mobileBatchSurface.value === 'library') {
    return {
      collections: [] as string[],
      tours: libraryStore.tours.map((tour) => tour.clientId),
      pois: [] as string[],
      photos: [] as string[],
    }
  }
  if (mobileBatchSurface.value === 'collections-overview') {
    return {
      collections: searchWorkspaceStore.allWorkspaces.map((workspace) => workspace.clientId),
      tours: [] as string[],
      pois: [] as string[],
      photos: [] as string[],
    }
  }
  if (mobileBatchSurface.value === 'collection-detail' || mobileBatchSurface.value === 'pinned') {
    return {
      collections: [] as string[],
      tours: workspaceStore.tours.map((tour) => tour.clientId),
      pois: workspaceStore.allWorkspacePOIs.map((poi) => poi.clientId),
      photos: photosSortedByDate.value.map((photo) => photo.clientId),
    }
  }
  return {
    collections: [] as string[],
    tours: [] as string[],
    pois: [] as string[],
    photos: [] as string[],
  }
})
const currentBatchSelectableCount = computed(
  () =>
    currentBatchSelectableIds.value.collections.length +
    currentBatchSelectableIds.value.tours.length +
    currentBatchSelectableIds.value.pois.length +
    currentBatchSelectableIds.value.photos.length,
)
const areAllCurrentBatchItemsSelected = computed(() => {
  const ids = currentBatchSelectableIds.value
  if (currentBatchSelectableCount.value === 0) return false
  return (
    ids.collections.every((id) => mobileBatchSelectionStore.collections.has(id)) &&
    ids.tours.every((id) => mobileBatchSelectionStore.tours.has(id)) &&
    ids.pois.every((id) => mobileBatchSelectionStore.pois.has(id)) &&
    ids.photos.every((id) => mobileBatchSelectionStore.photos.has(id))
  )
})
const pinCapableSelectedCount = computed(() => {
  if (mobileBatchSurface.value === 'collections-overview') {
    return selectedOverviewCollections.value.length
  }
  return selectedBatchTours.value.length + selectedWorkspacePOIs.value.length
})
const selectedBatchPhotoCount = computed(() => selectedPhotos.value.length)
const hideCapableSelectedCount = computed(() => {
  if (mobileBatchSurface.value === 'collections-overview') {
    return selectedOverviewCollections.value.length
  }
  return (
    selectedWorkspaceTours.value.length +
    selectedWorkspacePOIs.value.length +
    selectedPhotos.value.length
  )
})
const allPinCapableSelectedItemsPinned = computed(() => {
  if (pinCapableSelectedCount.value === 0) return false
  if (mobileBatchSurface.value === 'collections-overview') {
    return selectedOverviewCollections.value.every((workspace) => workspace.isPinned === true)
  }
  const selectedPOIsArePinned = selectedWorkspacePOIs.value.every((poi) => poi.isPinned === true)
  return selectedBatchTours.value.every((tour) => tour.isPinned === true) && selectedPOIsArePinned
})
const mobileBatchPinLabel = computed(() =>
  allPinCapableSelectedItemsPinned.value ? 'Unpin' : 'Pin',
)
const allHideCapableSelectedItemsVisible = computed(() => {
  if (hideCapableSelectedCount.value === 0) return false
  if (mobileBatchSurface.value === 'collections-overview') {
    return selectedOverviewCollections.value.every((workspace) =>
      isOverviewCollectionVisible(workspace.clientId),
    )
  }
  const selectedToursVisible = selectedWorkspaceTours.value.every((tour) => tour.visible !== false)
  const selectedPOIsVisible =
    selectedWorkspacePOIs.value.length === 0 || uiStore.arePOIsVisible === true
  const selectedPhotosVisible =
    selectedPhotos.value.length === 0 || uiStore.arePhotosVisible === true
  return selectedToursVisible && selectedPOIsVisible && selectedPhotosVisible
})
const mobileBatchHideLabel = computed(() =>
  allHideCapableSelectedItemsVisible.value ? 'Hide' : 'Unhide',
)

const mobileBatchPermissionState = computed(() =>
  resolveMobileBatchPermissionState({
    surface: mobileBatchSurface.value,
    selectedTours: selectedBatchTours.value,
    selectedPOIs: selectedWorkspacePOIs.value,
    selectedPhotos: selectedPhotos.value,
    selectedCollections: selectedOverviewCollections.value,
    isCollectionOwner: workspaceStore.isOwner,
    permissions: {
      deleteTour: hasPermission('delete_tour'),
      deleteWaypoint: hasPermission('delete_waypoint'),
      deletePhoto: hasPermission('delete_photo'),
    },
  }),
)
const mobileBatchDeleteHasPermissionConflict = computed(
  () => mobileBatchPermissionState.value.deleteHasPermissionConflict,
)
const mobileBatchRemoveHasPermissionConflict = computed(
  () => mobileBatchPermissionState.value.removeHasPermissionConflict,
)
const hasSelectedAddToCollectionPayload = computed(
  () => mobileBatchPermissionState.value.canAddToCollection,
)
const addToCollectionDisabledReason = computed(
  () => mobileBatchPermissionState.value.addToCollectionDisabledReason,
)

const formatBatchCount = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`

const formatSkippedBatchMessage = (skipped: Array<[number, string, string?]>) => {
  const parts = skipped
    .filter(([count]) => count > 0)
    .map(([count, singular, plural]) => formatBatchCount(count, singular, plural))
  return parts.length > 0 ? `Skipped ${parts.join(', ')}.` : ''
}

const showSkippedBatchInfo = (skipped: Array<[number, string, string?]>) => {
  const message = formatSkippedBatchMessage(skipped)
  if (message) showInfo(message)
}

const enterMobileBatchSelection = () => {
  const surface = mobileBatchSurface.value
  if (!surface) return
  mobileBatchSelectionStore.enter(surface)
  if (surface === 'library') {
    libraryStore.clearSelectedTours()
    libraryStore.enterSelectionMode()
  }
}

const toggleSelectAllMobileBatchItems = () => {
  const ids = currentBatchSelectableIds.value
  if (areAllCurrentBatchItemsSelected.value) {
    mobileBatchSelectionStore.clear()
    if (mobileBatchSurface.value === 'library') {
      libraryStore.clearSelectedTours()
    }
    return
  }

  mobileBatchSelectionStore.setSelected('collections', ids.collections)
  mobileBatchSelectionStore.setSelected('tours', ids.tours)
  mobileBatchSelectionStore.setSelected('pois', ids.pois)
  mobileBatchSelectionStore.setSelected('photos', ids.photos)
  if (mobileBatchSurface.value === 'library') {
    libraryStore.setSelectedTourIds(ids.tours)
  }
}

const exitMobileBatchSelection = () => {
  mobileBatchSelectionStore.exit()
  libraryStore.exitSelectionMode()
}

watch(mobileBatchSurface, (surface) => {
  if (!mobileBatchSelectionStore.activeSurface) return
  if (surface !== mobileBatchSelectionStore.activeSurface) {
    exitMobileBatchSelection()
  }
})

const handleOpenMobileTourDetail = (tour: TourListItem) => {
  mobileTourDetailTour.value = tour
  mobileDetail.value = null
  currentView.value = 'all-activities-tour-detail'
  const cached = activityRouteCollectionRef.value?.expandedRowDetails[tour.clientId]
  if (cached) {
    mobileDetail.value = cached
    mobileDetailLoading.value = false
  } else {
    mobileDetailLoading.value = true
    void activityRouteCollectionRef.value?.loadExpandedRowDetail(tour.clientId)
  }
}

const handleTourDetailLoaded = (tourId: string) => {
  if (mobileTourDetailTour.value?.clientId === tourId) {
    mobileDetail.value = activityRouteCollectionRef.value?.expandedRowDetails[tourId] ?? null
    mobileDetailLoading.value = false
  }
}

const mobileTourDetailEffectiveName = computed(() => {
  const tour = mobileDetail.value ?? mobileTourDetailTour.value
  return tour ? getEffectiveTourName(tour) : ''
})
const mobileTourDetailNameSubtitle = computed(() => {
  const tour = mobileDetail.value ?? mobileTourDetailTour.value
  return tour ? getTourGlobalNameSubtitle(tour) : null
})

const openMobileRename = () => {
  const tour = mobileTourDetailTour.value
  if (!tour) return
  openMobileRenameSheet({
    title: `Rename ${tour.type || tour.startDate ? 'activity' : 'route'}`,
    currentName: mobileTourDetailEffectiveName.value,
    submit: async (name) => {
      if (name === mobileTourDetailEffectiveName.value) return
      await libraryStore.updateTour(tour.clientId, { name })
      tour.name = name
      if (mobileDetail.value) mobileDetail.value.name = name
    },
  })
}

// ── Mobile tour detail helpers ────────────────────────────────────────────────

const mobileDetail = ref<Tour | null>(null)
const mobileDetailLoading = ref(false)
const visibleCollectionElevationGain = computed(() => {
  let totalGain: number | null = null
  for (const tour of workspaceStore.tours) {
    for (const track of tour.tracks) {
      if (shouldHideAscentForTrack(track, tour.type)) continue
      const gain = getTrackElevationGain(track)
      if (gain && gain > 0) totalGain = (totalGain ?? 0) + gain
    }
  }
  return totalGain
})
const workspaceInfoDateRange = computed(() => {
  const activityTimes = workspaceStore.tours
    .flatMap((tour) => tour.tracks.map((track) => track.startTime))
    .filter((startTime): startTime is number => startTime != null && Number.isFinite(startTime))
  if (activityTimes.length === 0) return null

  const firstActivity = new Date(Math.min(...activityTimes))
  const lastActivity = new Date(Math.max(...activityTimes))
  const formatFullDate = (date: Date) =>
    date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  const formatDateWithoutYear = (date: Date) =>
    date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })

  if (firstActivity.toDateString() === lastActivity.toDateString()) {
    return formatFullDate(firstActivity)
  }
  if (firstActivity.getFullYear() === lastActivity.getFullYear()) {
    return `${formatDateWithoutYear(firstActivity)} – ${formatFullDate(lastActivity)}`
  }
  return `${formatFullDate(firstActivity)} – ${formatFullDate(lastActivity)}`
})
const workspaceInfoStats = computed(() => {
  const stats: string[] = []
  if (workspaceStore.totalDistance > 0) {
    stats.push(formatCollectionDistance(workspaceStore.totalDistance))
  }
  if (visibleCollectionElevationGain.value !== null) {
    stats.push(`+${Math.round(visibleCollectionElevationGain.value)} m`)
  }
  const elapsedDuration = formatCollectionDuration(workspaceStore.totalTimeElapsed)
  if (elapsedDuration) stats.push(elapsedDuration)
  return stats
})
const hideMobileTourAscent = computed(() => {
  if (mobileDetail.value) return shouldHideAscentForTour(mobileDetail.value)
  return shouldHideAscentForTourListItem(mobileTourDetailTour.value)
})
const mobileTourDetailElevationGain = computed(() => {
  if (hideMobileTourAscent.value) return null
  return mobileDetail.value?.elevationGain ?? mobileTourDetailTour.value?.elevationGain ?? null
})
const getVisibleMobileTrackElevationGain = (track: Track): number | null => {
  const tourType = mobileDetail.value?.type ?? mobileTourDetailTour.value?.type ?? null
  if (shouldHideAscentForTrack(track, tourType)) return null
  return getTrackElevationGain(track)
}
const mobileSourceTour = computed(() => mobileDetail.value?.sourceTour ?? null)
const mobileSourceTourPrimaryWorkspace = computed(
  () => mobileSourceTour.value?.workspaces?.[0] ?? null,
)
const mobileSourceTourHref = computed(() => {
  const source = mobileSourceTour.value
  if (!source) return '#'
  const workspace = mobileSourceTourPrimaryWorkspace.value
  if (!workspace) return router.resolve({ name: 'workspaces' }).href
  return router.resolve({
    name: 'tour-detail',
    params: { workspaceId: workspace.clientId, tourId: source.clientId },
  }).href
})
const mobileSourceFileLabel = computed(() => {
  const label = mobileDetail.value?.sourceFilename?.trim()
  if (
    mobileSourceTour.value ||
    !mobileDetail.value?.canDownloadOriginal ||
    !isDownloadableTourSourceFilename(label)
  ) {
    return undefined
  }
  return label
})

const openMobileSourceTour = (event: MouseEvent) => {
  const source = mobileSourceTour.value
  if (!source) return
  event.preventDefault()

  const workspace = mobileSourceTourPrimaryWorkspace.value
  if (workspace) {
    void router.push({
      name: 'tour-detail',
      params: { workspaceId: workspace.clientId, tourId: source.clientId },
    })
    return
  }

  handleOpenMobileTourDetail({
    clientId: source.clientId,
    name: source.name,
    description: null,
    type: null,
    distance: null,
    elevationGain: null,
    createdAt: null,
    updatedAt: null,
    startDate: null,
    endDate: null,
    workspaces: source.workspaces,
    tracks: [],
  })
}

const mobileDetailAvgSpeed = computed((): number | null => {
  const d = mobileDetail.value
  if (!d?.distance || d.distance === 0) return null
  let totalMoving = 0
  for (const track of d.tracks) {
    const tm = getTrackTimeMoving(track)
    if (tm != null) totalMoving += tm
  }
  if (totalMoving === 0) return null
  return d.distance / 1000 / (totalMoving / 3600)
})

const mobileDetailStartTime = computed((): number | null => {
  const d = mobileDetail.value
  if (!d) return null
  let earliest: number | null = null
  for (const track of d.tracks) {
    if (track.startTime != null && (earliest === null || track.startTime < earliest)) {
      earliest = track.startTime
    }
  }
  return earliest
})

const mobileDetailEndTime = computed((): number | null => {
  const d = mobileDetail.value
  if (!d) return null
  let latest: number | null = null
  for (const track of d.tracks) {
    if (track.endTime != null && (latest === null || track.endTime > latest)) {
      latest = track.endTime
    }
  }
  return latest
})

const formatMobileDistance = (meters: number | null | undefined): string => {
  if (meters == null) return '—'
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`
  return `${Math.round(meters)} m`
}

const formatMobileDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  const isCurrentYear = d.getFullYear() === new Date().getFullYear()
  if (isCurrentYear) return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatMobileDuration = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null => {
  if (!startDate || !endDate) return null
  const ms = Date.parse(endDate) - Date.parse(startDate)
  if (ms <= 0) return null
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const formatMobileDateTime = (ts: number | null): string => {
  if (!ts) return ''
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Description inline edit
const mobileDescriptionEditOpen = ref(false)
const mobileDescriptionEditValue = ref('')
const mobileDescriptionTextareaRef = ref<HTMLTextAreaElement | null>(null)

const startMobileDescriptionEdit = (currentValue: string) => {
  mobileDescriptionEditValue.value = currentValue
  mobileDescriptionEditOpen.value = true
  nextTick(() => mobileDescriptionTextareaRef.value?.focus())
}

const saveMobileDescriptionEdit = async () => {
  const tour = mobileTourDetailTour.value
  if (!tour) {
    mobileDescriptionEditOpen.value = false
    return
  }
  const value = mobileDescriptionEditValue.value.trim()
  mobileDescriptionEditOpen.value = false
  try {
    await libraryStore.updateTour(tour.clientId, { description: value })
    if (mobileDetail.value) mobileDetail.value.description = value
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update tour description', { error })
    showError(`Failed to save: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const cancelMobileDescriptionEdit = () => {
  mobileDescriptionEditOpen.value = false
  mobileDescriptionEditValue.value = ''
}

// Track action sheet
const mobileTrackActionOpen = ref(false)
const mobileTrackActionTour = ref<TourListItem | null>(null)
const mobileTrackActionTrack = ref<Track | null>(null)
const mobileTrackActionTi = ref<number>(0)
const mobileTrackDownloading = ref<Set<string>>(new Set())
const isWorkspaceGpxDownloadModalOpen = ref(false)
const isWorkspaceGpxDownloading = ref(false)

const openMobileTrackActionSheet = (tour: TourListItem, track: Track, ti: number) => {
  mobileTrackActionTour.value = tour
  mobileTrackActionTrack.value = track
  mobileTrackActionTi.value = ti
  mobileTrackActionOpen.value = true
}

const closeMobileTrackActionSheet = () => {
  mobileTrackActionOpen.value = false
  mobileTrackActionTour.value = null
  mobileTrackActionTrack.value = null
}

const mobileDownloadTrack = async () => {
  const track = mobileTrackActionTrack.value
  const ti = mobileTrackActionTi.value
  closeMobileTrackActionSheet()
  if (!track?.clientId) return
  mobileTrackDownloading.value = new Set([...mobileTrackDownloading.value, track.clientId])
  try {
    const blob = await apiService.downloadTrackGpx(track.clientId)
    const name = track.name?.trim() || `Track ${ti + 1}`
    await downloadFile(blob, `${name}.gpx`, { mimeType: 'application/gpx+xml' })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download track GPX', { error })
    showError('Failed to download track GPX')
  } finally {
    const next = new Set(mobileTrackDownloading.value)
    next.delete(track.clientId!)
    mobileTrackDownloading.value = next
  }
}

const closeWorkspaceGpxDownloadModal = () => {
  isWorkspaceGpxDownloadModalOpen.value = false
}

const handleWorkspaceGpxDownload = async (options: { includeRoutePoints: boolean }) => {
  if (!workspaceStore.clientId) return

  isWorkspaceGpxDownloading.value = true
  try {
    const [blob] = await Promise.all([
      apiService.downloadWorkspaceGpx(workspaceStore.clientId, {
        includeRoutePoints: options.includeRoutePoints,
      }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ])
    const name = workspaceStore.name.trim() || 'collection'
    await downloadFile(blob, `${name}.gpx`, { mimeType: 'application/gpx+xml' })
    closeWorkspaceGpxDownloadModal()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download collection GPX', { error })
    showError('Failed to download collection GPX')
  } finally {
    isWorkspaceGpxDownloading.value = false
  }
}

const handleMobileBatchPinToggle = async () => {
  const shouldUnpin = allPinCapableSelectedItemsPinned.value
  const selectedItemCount = pinCapableSelectedCount.value
  try {
    if (mobileBatchSurface.value === 'collections-overview') {
      const details = await Promise.all(
        selectedOverviewCollections.value.map((collection) =>
          apiService.getWorkspaceDetail(collection.clientId),
        ),
      )
      await setPinnedItemsBatch(details, !shouldUnpin)
      for (const collection of selectedOverviewCollections.value) {
        searchWorkspaceStore.updateWorkspacePinned(collection.clientId, !shouldUnpin)
      }
      showSuccess(
        `${shouldUnpin ? 'Unpinned' : 'Pinned'} ${formatBatchCount(selectedOverviewCollections.value.length, 'collection')}.`,
      )
      return true
    }

    await setPinnedItemsBatch(
      [{ tours: selectedBatchTours.value, pois: selectedWorkspacePOIs.value }],
      !shouldUnpin,
    )
    showSuccess(
      `${shouldUnpin ? 'Unpinned' : 'Pinned'} ${formatBatchCount(selectedItemCount, 'item')}.`,
    )
    showSkippedBatchInfo([[selectedBatchPhotoCount.value, 'photo']])
    return true
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update selected Pinned items', { error })
    showError(`Failed to update Pinned: ${error instanceof Error ? error.message : String(error)}`)
    return false
  }
}

const handleMobileBatchVisibilityToggle = async () => {
  const shouldShow = !allHideCapableSelectedItemsVisible.value
  const nextVisible = shouldShow
  if (mobileBatchSurface.value === 'collections-overview') {
    const selectedIds = new Set(mobileBatchSelectionStore.collections)
    selectedOverviewCollections.value.forEach((workspace) => {
      if (selectedIds.has(workspace.clientId)) {
        searchWorkspaceStore.setWorkspaceVisibility(workspace.clientId, nextVisible)
      }
    })
    showSuccess(
      `${shouldShow ? 'Unhid' : 'Hid'} ${formatBatchCount(selectedIds.size, 'collection')}.`,
    )
    return
  }
  for (const tour of selectedWorkspaceTours.value) {
    await workspaceStore.setTourVisibility(tour.clientId, nextVisible)
  }
  if (selectedWorkspacePOIs.value.length > 0) uiStore.setPOIsVisible(nextVisible)
  if (selectedPhotos.value.length > 0) uiStore.setPhotosVisible(nextVisible)
  showSuccess(
    `${shouldShow ? 'Unhid' : 'Hid'} ${formatBatchCount(hideCapableSelectedCount.value, 'item')}.`,
  )
}

const handleMobileBatchDelete = async () => {
  if (!mobileBatchSelectionStore.hasSelection) return
  if (mobileBatchDeleteHasPermissionConflict.value) {
    showInfo('Insufficient permissions')
    return
  }
  if (isSingleTourOnlyMobileBatchSelection.value) {
    mobileBatchSingleDeleteTour.value = selectedBatchTours.value[0]
    return
  }
  const confirmed = await showConfirm({
    title: 'Delete selected items',
    message: `Delete ${formatBatchCount(mobileBatchSelectionStore.selectedCount, 'selected item')}? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (!confirmed) return

  try {
    if (mobileBatchSurface.value === 'collections-overview') {
      for (const collection of selectedOverviewCollections.value) {
        await apiService.deleteWorkspace(collection.clientId)
      }
      showSuccess(
        `Deleted ${formatBatchCount(selectedOverviewCollections.value.length, 'collection')}.`,
      )
      exitMobileBatchSelection()
      return
    }
    for (const tour of selectedBatchTours.value) {
      await libraryStore.deleteTour(tour.clientId)
    }
    for (const poi of selectedWorkspacePOIs.value) {
      await workspaceStore.deleteWorkspacePOI(poi.clientId)
    }
    for (const photo of selectedPhotos.value) {
      await photoStore.clearPhoto(photo.clientId)
    }
    showSuccess(`Deleted ${formatBatchCount(mobileBatchSelectionStore.selectedCount, 'item')}.`)
    exitMobileBatchSelection()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to delete selected items', { error })
    showError(`Failed to delete: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const closeMobileBatchSingleDelete = () => {
  if (isMobileBatchSingleDeleting.value) return
  mobileBatchSingleDeleteTour.value = null
}

const confirmMobileBatchSingleDelete = async () => {
  const tour = mobileBatchSingleDeleteTour.value
  if (!tour) return

  isMobileBatchSingleDeleting.value = true
  try {
    await libraryStore.deleteTour(tour.clientId)
    const workspaceTourIndex = workspaceStore.tours.findIndex(
      (candidate) => candidate.clientId === tour.clientId,
    )
    if (workspaceTourIndex !== -1) {
      workspaceStore.tours.splice(workspaceTourIndex, 1)
    }
    if (workspaceStore._selectedTourId === tour.clientId) {
      workspaceStore.setSelectedTourId(null)
    }
    showSuccess('Deleted 1 item.')
    mobileBatchSingleDeleteTour.value = null
    exitMobileBatchSelection()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to delete selected tour', {
      error,
      tourId: tour.clientId,
    })
    showError(`Failed to delete: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    isMobileBatchSingleDeleting.value = false
  }
}

const handleMobileBatchAddToCollection = () => {
  if (!hasSelectedAddToCollectionPayload.value) return
  openAddToCollection({
    tours: selectedBatchTours.value.map(tourToAddToCollectionItem),
    points: selectedWorkspacePOIs.value.map((poi) => ({
      clientId: poi.clientId,
      workspaceClientId: poi.workspaceClientId,
      lat: poi.lat,
      lon: poi.lon,
      ele: poi.ele,
      name: poi.name,
      description: poi.description,
      time: poi.time,
      type: poi.type,
    })),
    sourceWorkspaceId: workspaceStore.clientId,
    onComplete: () => exitMobileBatchSelection(),
  })
}

const handleMobileBatchRemove = async () => {
  if (mobileBatchSurface.value !== 'collection-detail' || !workspaceStore.clientId) return
  const removableCount = selectedWorkspaceTours.value.length + selectedWorkspacePOIs.value.length
  const skippedPhotoCount = selectedPhotos.value.length
  if (removableCount === 0) return
  if (mobileBatchRemoveHasPermissionConflict.value) {
    showInfo('Insufficient permissions')
    return
  }
  const confirmed = await showConfirm({
    title: 'Remove selected items',
    message: `Remove ${formatBatchCount(mobileBatchSelectionStore.selectedCount, 'selected item')} from this collection?`,
    confirmText: 'Remove',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (!confirmed) return

  try {
    for (const tour of selectedWorkspaceTours.value) {
      await workspaceStore.clearTour(tour.clientId)
    }
    for (const poi of selectedWorkspacePOIs.value) {
      await workspaceStore.removeWorkspacePOIFromCurrentWorkspace(poi.clientId)
    }
    showSuccess(`Removed ${formatBatchCount(removableCount, 'item')}.`)
    showSkippedBatchInfo([[skippedPhotoCount, 'photo']])
    exitMobileBatchSelection()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to remove selected items from collection', {
      error,
    })
    showError(`Failed to remove: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const handleMobileBatchMakeOffline = async () => {
  if (!isTauriApp || selectedOverviewCollections.value.length === 0) return
  try {
    for (const collection of selectedOverviewCollections.value) {
      const detail = await apiService.getWorkspaceDetail(collection.clientId)
      const offlineWorkspace: Workspace = {
        ...detail,
        markedForDeletion: detail.markedForDeletion ?? false,
        deletionScheduledAt: detail.deletionScheduledAt ?? null,
      }
      const offlinePhotos: Photo[] = (detail.photos ?? []).map((photo) => ({
        clientId: photo.clientId,
        name: photo.name ?? 'Photo',
        hasCoordinates: photo.hasCoordinates,
        coordinateSource: photo.coordinateSource,
        lat: photo.lat,
        lon: photo.lon,
        takenAt: photo.takenAt,
        canEdit: photo.canEdit,
        uploadedByName: photo.uploadedByName,
        _binaryDataUrl: photo.url ?? undefined,
        _thumbnailUrl: photo.thumbnailUrl ?? null,
      }))
      await offlineStore.makeOffline(
        offlineWorkspace,
        detail.tours,
        offlinePhotos,
        detail.pois ?? [],
        currentStylePreference.value,
        {
          includePhotos: offlineStore.getSavedIncludePhotos(),
          includeTerrain: offlineStore.getSavedIncludeTerrain(),
        },
      )
    }
    showSuccess(
      `Started offline download for ${formatBatchCount(selectedOverviewCollections.value.length, 'collection')}.`,
    )
  } catch (error) {
    logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to make selected collections offline', {
      error,
    })
    showError(`Failed to make offline: ${error instanceof Error ? error.message : String(error)}`)
  }
}

type MobileBatchAction = {
  key: string
  label: string
  disabled: boolean
  disabledReason?: string
  danger?: boolean
  onClick: () => void | boolean | Promise<void | boolean>
}

type MobileBatchActionBarItem = MobileBatchAction & {
  icon: Component
  tone?: 'amber' | 'danger' | 'default'
}

const mobileBatchActions = computed(() => {
  const surface = mobileBatchSurface.value
  const actions: MobileBatchAction[] = []
  if (!surface) return actions

  const hasItemSelection = mobileBatchSelectionStore.selectedCount > 0

  if (surface === 'library') {
    actions.push(
      {
        key: 'add-to-collection',
        label: 'Add to collection',
        disabled: selectedBatchTours.value.length === 0,
        onClick: handleMobileBatchAddToCollection,
      },
      {
        key: 'delete',
        label: 'Delete',
        disabled:
          selectedBatchTours.value.length === 0 || mobileBatchDeleteHasPermissionConflict.value,
        disabledReason: mobileBatchDeleteHasPermissionConflict.value
          ? 'Insufficient permissions'
          : undefined,
        danger: true,
        onClick: handleMobileBatchDelete,
      },
    )
  } else if (surface === 'collections-overview') {
    actions.push(
      {
        key: 'hide',
        label: mobileBatchHideLabel.value,
        disabled: selectedOverviewCollections.value.length === 0,
        onClick: handleMobileBatchVisibilityToggle,
      },
      ...(isTauriApp
        ? [
            {
              key: 'make-offline',
              label: 'Make available offline',
              disabled: selectedOverviewCollections.value.length === 0,
              onClick: handleMobileBatchMakeOffline,
            },
          ]
        : []),
      {
        key: 'delete',
        label: 'Delete',
        disabled:
          selectedOverviewCollections.value.length === 0 ||
          mobileBatchDeleteHasPermissionConflict.value,
        disabledReason: mobileBatchDeleteHasPermissionConflict.value
          ? 'Insufficient permissions'
          : undefined,
        danger: true,
        onClick: handleMobileBatchDelete,
      },
    )
  } else {
    actions.push(
      {
        key: 'hide',
        label: mobileBatchHideLabel.value,
        disabled: hideCapableSelectedCount.value === 0,
        onClick: handleMobileBatchVisibilityToggle,
      },
      {
        key: 'add-to-collection',
        label: 'Add to collection',
        disabled: !hasSelectedAddToCollectionPayload.value,
        disabledReason: addToCollectionDisabledReason.value,
        onClick: handleMobileBatchAddToCollection,
      },
    )
    if (surface === 'collection-detail') {
      actions.push({
        key: 'remove',
        label: 'Remove',
        disabled:
          selectedWorkspaceTours.value.length + selectedWorkspacePOIs.value.length === 0 ||
          mobileBatchRemoveHasPermissionConflict.value,
        disabledReason: mobileBatchRemoveHasPermissionConflict.value
          ? 'Insufficient permissions'
          : undefined,
        danger: true,
        onClick: handleMobileBatchRemove,
      })
    }
    actions.push({
      key: 'delete',
      label: 'Delete',
      disabled: !hasItemSelection || mobileBatchDeleteHasPermissionConflict.value,
      disabledReason: mobileBatchDeleteHasPermissionConflict.value
        ? 'Insufficient permissions'
        : undefined,
      danger: true,
      onClick: handleMobileBatchDelete,
    })
  }
  return actions
})

const getMobileBatchActionIcon = (key: string): Component => {
  switch (key) {
    case 'add-to-collection':
      return SquarePlusIcon
    case 'hide':
      return mobileBatchHideLabel.value === 'Unhide' ? EyeIcon : EyeOffIcon
    case 'make-offline':
      return DownloadIcon
    case 'remove':
      return FolderMinusIcon
    case 'delete':
      return TrashIcon
    default:
      return CheckIcon
  }
}

const mobileBatchActionBarItems = computed<MobileBatchActionBarItem[]>(() => [
  {
    key: 'pin',
    label: mobileBatchPinLabel.value,
    disabled: pinCapableSelectedCount.value === 0,
    icon: allPinCapableSelectedItemsPinned.value ? PinOffIcon : PinIcon,
    tone: 'amber',
    onClick: handleMobileBatchPinToggle,
  },
  ...mobileBatchActions.value.map((action) => ({
    ...action,
    icon: getMobileBatchActionIcon(action.key),
    tone: action.danger ? ('danger' as const) : ('default' as const),
  })),
])

const mobileBatchActionBarBottom = computed(() => `max(${safeAreaInsets.value.bottom}, 10px)`)

const runMobileBatchAction = async (action: MobileBatchAction) => {
  if (action.disabled) return
  const succeeded = await action.onClick()
  if (succeeded === false) return
  if (action.key === 'add-to-collection') return
  exitMobileBatchSelection()
}

const mobileDeleteTrack = async () => {
  const track = mobileTrackActionTrack.value
  const tour = mobileTrackActionTour.value
  const ti = mobileTrackActionTi.value
  closeMobileTrackActionSheet()
  if (!track || !tour) return
  await activityRouteCollectionRef.value?.deleteExpandedTrack(tour.clientId, tour, track, ti)
}

const openMobileAddToCol = (tour: TourListItem) => {
  openAddToCollection({ tours: [tourToAddToCollectionItem(tour)] })
}

const mobileRemoveFromWorkspace = async (tour: TourListItem, workspaceClientId: string) => {
  await activityRouteCollectionRef.value?.removeFromWorkspace(tour, workspaceClientId)
}
const selectedWorkspaceName = ref<string>(
  hasActiveWorkspace ? workspaceStore.name || 'Untitled Collection' : '',
)

// Drive isAllActivitiesView from navigation state, not component lifecycle,
// so tracks only appear when the user is actually in the all-activities view.
watch(
  () => currentView.value,
  (val) => {
    uiStore.isAllActivitiesView = val === 'all-activities' || val === 'all-activities-tour-detail'
  },
  { immediate: true },
)
// Set to true when the toolbar itself initiates the transition to overview, so the
// clientId watcher knows not to collapse the panel (the toolbar manages that itself).
const isToolbarNavigatingToOverview = ref(false)
// The collection clientId we've already evaluated for the "open the drawer tall when empty"
// one-shot, so the tall open fires once per collection entry and never re-snaps on hasTours
// changes (dragging down / adding the first item must not yank the height).
const tallOpenEvaluatedClientId = ref<string | null>(null)
const isShareModalOpen = ref(false)
const isWorkspaceInfoSheetOpen = ref(false)
const workspaceInfoSheetExpandedHeight = ref<number | undefined>()
const workspaceInfoSheetHighHeight = ref<number | undefined>()
const workspaceInfoSheetInitialSnap = ref<'medium' | 'high'>('medium')
const workspaceInfoMembers = ref<WorkspaceMember[]>([])
const isWorkspaceInfoMembersLoading = ref(false)
let workspaceInfoMembersRequestId = 0
const isWorkspaceInfoNameEditing = ref(false)
const workspaceInfoNameDraft = ref('')
const workspaceInfoNameError = ref('')
const isWorkspaceInfoNameSaving = ref(false)
const isWorkspaceInfoDescriptionEditing = ref(false)
const workspaceInfoDescriptionDraft = ref('')
const workspaceInfoDescriptionError = ref('')
const isWorkspaceInfoDescriptionSaving = ref(false)
const canRenameWorkspaceInfo = computed(
  () => !workspaceStore.isSystem && hasPermission('rename_workspace'),
)
const canEditWorkspaceInfoDescription = computed(
  () => !workspaceStore.isSystem && hasPermission('edit_workspace_description'),
)
const workspaceInfoMemberNames = computed(() => {
  const names = [
    ...workspaceInfoMembers.value
      .filter((member) => member.role !== 0 && member.roleDisplay.toLocaleLowerCase() !== 'owner')
      .map((member) => member.name),
    workspaceStore.isOwner ? '' : (authStore.user?.name ?? ''),
  ]
    .map((name) => name.trim())
    .filter(Boolean)
  return [...new Map(names.map((name) => [name.toLocaleLowerCase(), name])).values()]
})
const showWorkspaceInfoMembers = computed(
  () =>
    !workspaceStore.isPublic &&
    workspaceInfoMembers.value.length > 1 &&
    workspaceInfoMemberNames.value.length > 0,
)
const showDropdownMenu = ref(false) // State for dropdown menu
const isUploadDialogOpen = ref(false) // State for upload dialog
const isOfflineDialogOpen = ref(false)
const dropdownButtonRef = ref<HTMLElement>()
const dropdownMenuRef = ref<HTMLElement>()
const dropdownPosition = ref({ top: 0, left: 0 })
const currentStylePreference = computed<MapStylePreference>(
  () => mapStore.mapStylePreference ?? DEFAULT_MAP_STYLE_PREFERENCE,
)
const activeOfflineDownload = computed(() => offlineStore.active[workspaceStore.clientId])
const isCurrentWorkspaceOffline = computed(() => offlineStore.isOffline(workspaceStore.clientId))
const currentWorkspacePinnableItems = computed(() => [
  ...workspaceStore.tours,
  ...workspaceStore.allWorkspacePOIs,
])
const hasCurrentWorkspacePinnableItems = computed(
  () => currentWorkspacePinnableItems.value.length > 0,
)
const areAllCurrentWorkspaceItemsPinned = computed(
  () =>
    hasCurrentWorkspacePinnableItems.value &&
    currentWorkspacePinnableItems.value.every((item) => item.isPinned === true),
)

const handleCurrentWorkspacePinToggle = async () => {
  showDropdownMenu.value = false
  if (!hasCurrentWorkspacePinnableItems.value) return

  const shouldPin = !areAllCurrentWorkspaceItemsPinned.value
  if (!(await requireOnlineModificationAccess(shouldPin ? 'pin all items' : 'unpin all items'))) {
    return
  }

  try {
    await setPinnedItemsBatch(
      [{ tours: workspaceStore.tours, pois: workspaceStore.allWorkspacePOIs }],
      shouldPin,
    )
    showSuccess(shouldPin ? 'Pinned all items' : 'Unpinned all items')
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update collection Pinned items', {
      error,
      workspaceId: workspaceStore.clientId,
    })
    showError(`Failed to update Pinned: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const handleCurrentWorkspaceOffline = () => {
  showDropdownMenu.value = false
  emit('make-offline', workspaceStore.clientId)
}

const handleUnpinAll = async () => {
  showDropdownMenu.value = false
  if (!hasItemsToUnpin.value) return
  if (!(await requireOnlineModificationAccess('unpin all items'))) return

  const confirmed = await showConfirm({
    title: 'Unpin everything?',
    message:
      'This removes every activity and route from Pinned, plus POIs pinned from elsewhere. POIs and photos created directly in Pinned stay here. No data will be deleted.',
    confirmText: 'Unpin all',
    cancelText: 'Cancel',
    variant: 'warning',
  })
  if (!confirmed) return

  try {
    const result = await unpinAllPinnedItems({
      tours: workspaceStore.tours,
      pois: workspaceStore.allWorkspacePOIs,
    })
    if (result.failedCount > 0) {
      showError(
        `Unpinned ${result.unpinnedCount} item${result.unpinnedCount === 1 ? '' : 's'}. ${result.failedCount} item${result.failedCount === 1 ? '' : 's'} could not be unpinned and remain in Pinned.`,
      )
    } else {
      showSuccess('Unpinned all linked items')
    }
    panelHeightMode.value = 'collapsed'
    uiStore.collapseMobileBottomPanel()
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to clear Pinned', { error })
    showError(`Failed to clear Pinned: ${error instanceof Error ? error.message : String(error)}`)
  }
}

// ─────────────────────────────────────────────────────────────
// Collection Detail — search / sort (mirrors WorkspaceToolbar.vue)
// ─────────────────────────────────────────────────────────────

type TourSortBy = 'position' | 'name' | 'distance' | 'date'
type SortDir = 'asc' | 'desc'

const tourSearchQuery = ref('')
const tourSortBy = ref<TourSortBy>('position')
const tourSortDir = ref<SortDir>('asc')

const loadTourSortPreference = () => {
  const saved = settingsService.getSetting<{ sortBy?: TourSortBy; sortDir?: SortDir }>(
    UI_SETTING_KEYS.mapTourSort,
  )
  if (saved?.sortBy && saved.sortDir) {
    tourSortBy.value = saved.sortBy
    tourSortDir.value = saved.sortDir
  }
}

const saveTourSortPreference = () => {
  settingsService.saveSetting(UI_SETTING_KEYS.mapTourSort, {
    sortBy: tourSortBy.value,
    sortDir: tourSortDir.value,
  })
}

loadTourSortPreference()

settingsService.subscribe(({ key, value, source }) => {
  if (source !== 'remote') return
  if (key !== UI_SETTING_KEYS.mapTourSort || !value || typeof value !== 'object') return

  const saved = value as { sortBy?: TourSortBy; sortDir?: SortDir }
  if (saved.sortBy && saved.sortDir) {
    tourSortBy.value = saved.sortBy
    tourSortDir.value = saved.sortDir
  }
})

// Expandable search bar
const tourSearchExpanded = ref(false)
const tourSearchInputRef = ref<HTMLInputElement | null>(null)

const openTourSearch = async () => {
  closeTourSortMenu()
  tourSearchExpanded.value = true
  await nextTick()
  tourSearchInputRef.value?.focus()
}

const closeTourSearch = () => {
  tourSearchExpanded.value = false
  tourSearchQuery.value = ''
}

// Sort menu
const tourSortMenuOpen = ref(false)
const tourSortMenuStyle = ref<{ top?: string; bottom?: string; right: string }>({
  top: '0px',
  right: '0px',
})

const toggleTourSortMenu = (event: MouseEvent) => {
  if (tourSortMenuOpen.value) {
    tourSortMenuOpen.value = false
    return
  }
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const right = window.innerWidth - rect.right
  if (spaceBelow >= 200) {
    tourSortMenuStyle.value = { top: `${rect.bottom + 4}px`, right: `${right}px` }
  } else {
    tourSortMenuStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      right: `${right}px`,
    }
  }
  tourSortMenuOpen.value = true
}

const closeTourSortMenu = () => {
  tourSortMenuOpen.value = false
}

const tourSortOptions: Array<{ label: string; sortBy: TourSortBy; sortDir: SortDir }> = [
  { label: 'Default order', sortBy: 'position', sortDir: 'asc' },
  { label: 'Name (A to Z)', sortBy: 'name', sortDir: 'asc' },
  { label: 'Name (Z to A)', sortBy: 'name', sortDir: 'desc' },
  { label: 'Distance (short to long)', sortBy: 'distance', sortDir: 'asc' },
  { label: 'Distance (long to short)', sortBy: 'distance', sortDir: 'desc' },
  { label: 'Date (new to old)', sortBy: 'date', sortDir: 'desc' },
  { label: 'Date (old to new)', sortBy: 'date', sortDir: 'asc' },
]

const isActiveTourSort = (opt: { sortBy: TourSortBy; sortDir: SortDir }) =>
  tourSortBy.value === opt.sortBy && tourSortDir.value === opt.sortDir

const setTourSort = (opt: { sortBy: TourSortBy; sortDir: SortDir }) => {
  tourSortBy.value = opt.sortBy
  tourSortDir.value = opt.sortDir
  closeTourSortMenu()
  saveTourSortPreference()
}

// Upload dropdown
const tourUploadMenuOpen = ref(false)
const tourUploadMenuStyle = ref<{ top?: string; bottom?: string; left?: string; right?: string }>({
  top: '0px',
  left: '0px',
})

const toggleTourUploadMenu = (event: MouseEvent) => {
  if (tourUploadMenuOpen.value) {
    tourUploadMenuOpen.value = false
    return
  }
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const left = rect.left
  if (spaceBelow >= 80) {
    tourUploadMenuStyle.value = { top: `${rect.bottom + 4}px`, left: `${left}px` }
  } else {
    tourUploadMenuStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      left: `${left}px`,
    }
  }
  tourUploadMenuOpen.value = true
}

const closeTourUploadMenu = () => {
  tourUploadMenuOpen.value = false
}

const handleGpxUploadClick = () => {
  triggerGpxUpload()
  closeTourUploadMenu()
}

const handlePhotoUploadClick = () => {
  triggerPhotoUpload()
  closeTourUploadMenu()
}

// Filtered + sorted tour list; undefined means "no override" (show all in store order with drag)
const detailDisplayTours = computed(() => {
  const allTours = workspaceStore.allTours
  const query = tourSearchQuery.value.toLowerCase().trim()
  const isFiltering = query !== '' || tourSortBy.value !== 'position'

  if (!isFiltering) return undefined

  let result = [...allTours]

  if (query) {
    result = result.filter((t) => t.name.toLowerCase().includes(query))
  }

  result.sort((a, b) => {
    let cmp = 0
    switch (tourSortBy.value) {
      case 'name':
        cmp = a.name.localeCompare(b.name)
        break
      case 'distance': {
        const da = a.distance ?? 0
        const db = b.distance ?? 0
        cmp = da - db
        break
      }
      case 'date': {
        const ta = Math.min(
          ...a.tracks.map((t) => (typeof t.startTime === 'number' ? t.startTime : Infinity)),
        )
        const tb = Math.min(
          ...b.tracks.map((t) => (typeof t.startTime === 'number' ? t.startTime : Infinity)),
        )
        cmp = ta - tb
        break
      }
      default:
        cmp = (a.order ?? 0) - (b.order ?? 0)
    }
    return tourSortDir.value === 'asc' ? cmp : -cmp
  })

  return result
})

const filteredPhotoList = computed(() => photosSortedByDate.value ?? [])
const totalPhotoCount = computed(() => photosSortedByDate.value?.length ?? 0)
const filteredPhotoCount = computed(() => filteredPhotoList.value.length)
const isPhotoFiltering = computed(() => filteredPhotoCount.value !== totalPhotoCount.value)

// Delayed loading indicator (bike) for collection detail view
const routeWorkspaceId = computed(() =>
  typeof route.params.workspaceId === 'string' ? (route.params.workspaceId as string) : null,
)
const isDetailLoading = computed(
  () => routeWorkspaceId.value !== null && workspaceStore.clientId !== routeWorkspaceId.value,
)
const showLoadingIcon = ref(false)
let loadingIconTimer: number | null = null
// Delayed loading text (200ms) to avoid flicker
const showLoadingText = ref(false)
let loadingTextTimer: number | null = null

watch(isDetailLoading, (loading) => {
  if (loading) {
    if (loadingIconTimer) clearTimeout(loadingIconTimer)
    loadingIconTimer = window.setTimeout(() => {
      if (isDetailLoading.value) showLoadingIcon.value = true
    }, 800)

    if (loadingTextTimer) clearTimeout(loadingTextTimer)
    loadingTextTimer = window.setTimeout(() => {
      if (isDetailLoading.value) showLoadingText.value = true
    }, 200)
  } else {
    if (loadingIconTimer) {
      clearTimeout(loadingIconTimer)
      loadingIconTimer = null
    }
    showLoadingIcon.value = false

    if (loadingTextTimer) {
      clearTimeout(loadingTextTimer)
      loadingTextTimer = null
    }
    showLoadingText.value = false
  }
})

// Calculate dropdown position when it becomes visible
watch(showDropdownMenu, (isVisible) => {
  if (isVisible && dropdownButtonRef.value) {
    nextTick(() => {
      const buttonRect = dropdownButtonRef.value!.getBoundingClientRect()
      const menuHeight = dropdownMenuRef.value?.offsetHeight ?? 132
      const top = isMobileBottomPanelExpanded.value
        ? buttonRect.bottom + 4
        : Math.max(8, buttonRect.top - menuHeight - 4)
      dropdownPosition.value = {
        top,
        left: Math.max(16, buttonRect.right - 192), // 192px is w-48, 16px minimum margin
      }
    })
  }
})

// Handle click outside dropdown
const handleDropdownClickOutside = (event: Event) => {
  if (isOfflineDialogOpen.value) return

  if (
    showDropdownMenu.value &&
    dropdownButtonRef.value &&
    dropdownMenuRef.value &&
    !dropdownButtonRef.value.contains(event.target as Node) &&
    !dropdownMenuRef.value.contains(event.target as Node)
  ) {
    showDropdownMenu.value = false
  }
}

// Use Vue composable for conditional dropdown click outside
let removeDropdownClickListener: (() => void) | null = null

watch(showDropdownMenu, (isVisible) => {
  if (isVisible && typeof document !== 'undefined') {
    document.addEventListener('click', handleDropdownClickOutside)
    removeDropdownClickListener = () => {
      document.removeEventListener('click', handleDropdownClickOutside)
    }
  } else {
    if (removeDropdownClickListener) {
      removeDropdownClickListener()
      removeDropdownClickListener = null
    }
  }
})

// Track local modal states to update global dialog state
watch(
  [isShareModalOpen, isUploadDialogOpen, isOfflineDialogOpen],
  ([shareOpen, uploadOpen, offlineOpen]) => {
    const localDialogOpen = shareOpen || uploadOpen || offlineOpen
    if (localDialogOpen) {
      uiStore.setDialogOpen(true)
    }
    // Note: We don't set to false here to avoid race conditions
    // MapHeader.vue handles the main dialog state management
  },
)

// Touch detection (replace previous iOS-only detection)
const isTouch = isTouchDevice()

// Computed properties
const MOBILE_SELECTED_ROUTE_ACTION_HEIGHT = 64
const MOBILE_PANEL_TAP_DRAG_TOLERANCE = 8
const selectedRouteActionBarVisible = computed(() => false)
const toolbarHeaderHeight = computed(
  () => 64 + (selectedRouteActionBarVisible.value ? MOBILE_SELECTED_ROUTE_ACTION_HEIGHT : 0),
) // handle/header + optional selected-route actions

// All four primary surfaces share the same collapsed header height. Collection stats are currently
// not rendered in the peek, so collection detail has no reason to reserve extra vertical space.
const collapsedPanelHeight = computed(() => toolbarHeaderHeight.value)
// Aggregate stats row: shown at the collapsed peek AND the medium height — consistent with the
// track sheet, which keeps its stats as a persistent header. Hidden only at the high snap, where the
// photo cover carries the same stats in its overlay (so they're not duplicated).
const showCollectionStats = computed(() => false)
const showLegacyLibrarySelectionOverlay = false
const showLegacyCollectionActionBar = false
// The panel is an inset card with the SAME width as the tab bar (left-3 / right-3 = 12px).
// Its bottom sits flush on the bar's top edge (bar height 64, no gap) so the drawer appears to
// emerge from the bar, while the bar's transparent area below stays over the map — never the
// drawer content. Keep in sync with the bar's bottomOffset.
// Sit above the floating tab bar (not flush) so a sliver of map shows behind the bar and it reads
// as a separate translucent glass pill, not a fused white card. Injected from the map container so
// it collapses the tab bar's 64px when the bar is hidden at a detail level (collection drawer), and
// keeps every sheet in lock-step with the bar otherwise.
const floatingTabBarPanelOffset = inject<Ref<string>>(
  'floatingSheetBottomOffset',
  ref(FLOATING_SHEET_BOTTOM_OFFSET),
)
// Side inset so the card matches the tab bar width (left-3 / right-3 = 12px).
const floatingTabBarPanelSideInset = '12px'

// Use reactive window size for maxPanelHeight
const maxPanelHeight = computed(() => {
  if (typeof window !== 'undefined') {
    // Use window.innerHeight for better iOS Safari compatibility
    // Also account for any potential safe area insets
    const viewportHeight = window.innerHeight
    // Medium snap height (≈ half the screen). Kept in sync with the Route sheet's `mid`
    // (RouteDetailSheet) so both mobile drawers snap to the same positions.
    const calculated = Math.floor(viewportHeight * 0.52)

    return calculated
  }
  return 400 // Fallback value
})

const highPanelHeight = computed(() => {
  if (typeof window === 'undefined') return 620

  // `mobileSheetFullHeight` also clears the top safe-area (notch) so the high snap doesn't crowd
  // the clock on a notched iPhone.
  return Math.max(maxPanelHeight.value, mobileSheetFullHeight(window.innerHeight))
})

const panelHeight = computed(() => {
  // Dynamic height approach: show only the collapsed dock/header when collapsed, full when expanded
  const headerHeight = collapsedPanelHeight.value
  const maxHeight = maxPanelHeight.value
  const targetHeight = panelHeightMode.value === 'high' ? highPanelHeight.value : maxHeight

  if (isDragging.value) {
    return Math.min(
      highPanelHeight.value,
      Math.max(headerHeight, dragStartHeight.value - dragOffset.value),
    )
  }

  // Static heights:
  // - Expanded: full panel height
  // - Collapsed: only header height
  return isMobileBottomPanelExpanded.value ? targetHeight : headerHeight
})

const highTransitionProgress = computed(() => {
  const range = highPanelHeight.value - maxPanelHeight.value
  const visualHeight =
    isAnimating.value && renderedPanelHeight.value !== null
      ? renderedPanelHeight.value
      : panelHeight.value
  if (range <= 0) return visualHeight >= highPanelHeight.value ? 1 : 0
  return Math.min(1, Math.max(0, (visualHeight - maxPanelHeight.value) / range))
})
const expandedTransitionProgress = computed(() => {
  const range = maxPanelHeight.value - collapsedPanelHeight.value
  const visualHeight =
    isAnimating.value && renderedPanelHeight.value !== null
      ? renderedPanelHeight.value
      : panelHeight.value
  if (range <= 0) return visualHeight >= maxPanelHeight.value ? 1 : 0
  return Math.min(1, Math.max(0, (visualHeight - collapsedPanelHeight.value) / range))
})
const isCollectionHighTransition = computed(
  () =>
    currentView.value === 'workspace-detail' &&
    !isPinnedWorkspaceDetail.value &&
    highTransitionProgress.value > 0,
)
const collectionActionRowTop = computed(() =>
  currentView.value === 'workspace-detail' && !isPinnedWorkspaceDetail.value
    ? `${20 + 6 * highTransitionProgress.value}px`
    : undefined,
)
const collectionHighRevealProgress = computed(() =>
  Math.min(1, highTransitionProgress.value / 0.92),
)
const highHeaderButtonProgress = computed(() => Math.min(1, highTransitionProgress.value / 0.92))
const headerBackButtonProgress = computed(() =>
  currentView.value === 'workspace-detail' && !isPinnedWorkspaceDetail.value
    ? expandedTransitionProgress.value
    : highHeaderButtonProgress.value,
)
const collectionHighRevealStyle = computed(() => {
  const progress = collectionHighRevealProgress.value
  return {
    opacity: progress,
    clipPath: `inset(${(1 - progress) * 100}% 0 0 0)`,
    transform: `translate3d(0, ${16 * (1 - progress)}px, 0)`,
  }
})

const canShare = computed(() => {
  return workspaceStore.currentWorkspace?._createEventPushed === true && !workspaceStore.isSystem
})

// Handler for zoom to collections
const handleZoomToCollections = () => {
  const bounds = searchWorkspaceStore.getVisibleWorkspacesBounds()
  if (bounds) {
    // Re-enable auto-adaptation so the zoom is honoured. This is an explicit user
    // action (tapping the expanded "My Collections" header), so it's intentional.
    uiStore.enableAutoViewportAdaptation()
    uiStore.runIntentDrivenMapMovement('explicit', () => {
      window.dispatchEvent(
        new CustomEvent('zoom-to-search-workspaces', { detail: { bounds, force: true } }),
      )
    })
  }
}

const showFitMapButton = computed(() =>
  ['workspaces', 'all-activities', 'workspace-detail'].includes(currentView.value),
)

const canFitCurrentMapContext = computed(() => {
  if (!mapLoaded.value) return false
  if (currentView.value === 'workspaces') return hasOverviewMapContent.value
  if (currentView.value === 'all-activities') return libraryToursVisibleOnMap.value.length > 0
  if (currentView.value === 'workspace-detail') return hasWorkspaceMapContent.value
  return false
})

const moveHighPanelToExpandedForMapFit = () => {
  if (!isHighPanelHeader.value) return

  const rect = panelRef.value?.getBoundingClientRect()
  const bottomGap =
    rect && typeof window !== 'undefined' ? Math.max(0, window.innerHeight - rect.bottom) : 0
  uiStore.setMobileBottomPanelHeight(Math.round(bottomGap + maxPanelHeight.value))

  isAnimating.value = true
  panelHeightMode.value = 'expanded'
  uiStore.expandMobileBottomPanel()
  startPanelHeightTracking()
  resetAfterTransition()
}

const handleFitMapToCurrentContext = () => {
  if (!canFitCurrentMapContext.value) return

  moveHighPanelToExpandedForMapFit()

  if (currentView.value === 'workspaces') {
    handleZoomToCollections()
    return
  }

  uiStore.enableAutoViewportAdaptation()
  uiStore.runIntentDrivenMapMovement('explicit', () => {
    if (currentView.value === 'all-activities') {
      window.dispatchEvent(new CustomEvent('library-fit-visible-tours'))
    } else if (currentView.value === 'workspace-detail') {
      window.dispatchEvent(new CustomEvent('zoom-to-content'))
    }
  })
}

// Touch event handlers
const beginPanelTouchDrag = (clientY: number) => {
  const visibleHeight = panelRef.value?.getBoundingClientRect().height
  dragStartHeight.value =
    isAnimating.value && visibleHeight && visibleHeight > 0 ? visibleHeight : panelHeight.value
  isDragging.value = true
  isAnimating.value = false
  dragMode.value = 'touch'
  startY.value = clientY
  currentY.value = clientY
  startTime.value = Date.now()
  dragOffset.value = 0
}

const handleTouchStart = (event: TouchEvent) => {
  if (event.touches.length !== 1) return

  // Only prevent default for drag handle - allow normal touch for other elements
  const target = event.target as HTMLElement
  const isDragHandle = target.closest('.touch-none')
  const isInteractiveTarget = target.closest(
    'button, a, input, textarea, select, [role="button"], [contenteditable="true"]',
  )
  // Also allow drag-to-expand from the header area when the panel is collapsed.
  // We do NOT call preventDefault here so taps on header buttons still fire as clicks.
  const isHeaderArea = !isDragHandle && !isInteractiveTarget && target.closest('.panel-header')

  if (isDragHandle) {
    // Prevent default to avoid scroll bounce on iOS for drag handle
    event.preventDefault()

    beginPanelTouchDrag(event.touches[0].clientY)
  } else if (isHeaderArea) {
    beginPanelTouchDrag(event.touches[0].clientY)
  }
}

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging.value || event.touches.length !== 1) return

  // Only prevent default when actually dragging the panel
  event.preventDefault()
  currentY.value = event.touches[0].clientY
  dragOffset.value = currentY.value - startY.value
}

const getReleasePanelMode = (height: number, deltaY: number, velocity: number): PanelHeightMode => {
  const snapHeights = [collapsedPanelHeight.value, maxPanelHeight.value, highPanelHeight.value]
  const snapModes: PanelHeightMode[] = ['collapsed', 'expanded', 'high']
  const lowerMidpoint = (snapHeights[0] + snapHeights[1]) / 2
  const upperMidpoint = (snapHeights[1] + snapHeights[2]) / 2
  let snapIndex = height < lowerMidpoint ? 0 : height < upperMidpoint ? 1 : 2

  const velocityThreshold = 0.3
  const directCollapseVelocityThreshold = 1.4
  const directCollapseMinDistance = 128
  const startSnapIndex = snapModes.indexOf(panelHeightMode.value)
  if (
    velocity >= directCollapseVelocityThreshold &&
    panelHeightMode.value === 'high' &&
    deltaY >= directCollapseMinDistance
  ) {
    return 'collapsed'
  }
  if (velocity > velocityThreshold && snapIndex === startSnapIndex) {
    snapIndex = Math.min(2, Math.max(0, snapIndex + (deltaY < 0 ? 1 : -1)))
  }

  return snapModes[snapIndex]
}

const finishPanelDrag = () => {
  const deltaY = currentY.value - startY.value
  if (Math.abs(deltaY) < MOBILE_PANEL_TAP_DRAG_TOLERANCE) {
    isDragging.value = false
    dragOffset.value = 0
    dragMode.value = null
    return
  }

  const releaseHeight = panelHeight.value
  const deltaTime = Math.max(Date.now() - startTime.value, 1)
  const velocity = Math.abs(deltaY) / deltaTime
  const nextMode = getReleasePanelMode(releaseHeight, deltaY, velocity)

  isAnimating.value = true
  isDragging.value = false
  dragOffset.value = 0
  panelHeightMode.value = nextMode

  if (nextMode === 'collapsed') uiStore.collapseMobileBottomPanel()
  else uiStore.expandMobileBottomPanel()

  resetAfterTransition()
  dragMode.value = null
}

const resetScrollDragHandoff = () => {
  if (scrollDragScroller.value) {
    scrollDragScroller.value.style.overscrollBehaviorY = previousScrollerOverscrollBehaviorY
  }
  scrollDragCandidate.value = null
  scrollDragOwnsPanel.value = false
  scrollDragScroller.value = null
  previousScrollerOverscrollBehaviorY = ''
}

const handleScrollTouchStart = (event: TouchEvent) => {
  const target = event.target as HTMLElement
  const dragSurface = target.closest<HTMLElement>('[data-mobile-panel-drag-surface]')
  const scroller =
    target.closest<HTMLElement>('[data-mobile-panel-scroll]') ??
    dragSurface?.parentElement?.querySelector<HTMLElement>('[data-mobile-panel-scroll]') ??
    null
  if (
    event.touches.length !== 1 ||
    panelHeightMode.value === 'collapsed' ||
    !scroller ||
    (panelRef.value && !panelRef.value.contains(scroller)) ||
    scroller.scrollTop > 0
  ) {
    resetScrollDragHandoff()
    return
  }

  if (target.closest('input, textarea, select, [contenteditable="true"], [role="menu"]')) {
    resetScrollDragHandoff()
    return
  }

  // Menu triggers inside ordinary list content keep their gesture. Filter chips are the exception:
  // the actions row is an explicit drag surface, so a vertical gesture can hand off while taps and
  // horizontal chip scrolling remain untouched.
  if (!dragSurface && target.closest('[aria-haspopup="true"]')) {
    resetScrollDragHandoff()
    return
  }

  const touch = event.touches[0]
  scrollDragCandidate.value = {
    startX: touch.clientX,
    startY: touch.clientY,
    startTime: Date.now(),
  }
  scrollDragOwnsPanel.value = false
  scrollDragScroller.value = scroller
  previousScrollerOverscrollBehaviorY = scroller.style.overscrollBehaviorY
  scroller.style.overscrollBehaviorY = 'none'
}

const handleScrollTouchMove = (event: TouchEvent) => {
  if (event.touches.length !== 1) return

  if (scrollDragOwnsPanel.value && isDragging.value) {
    event.preventDefault()
    event.stopPropagation()
    currentY.value = event.touches[0].clientY
    dragOffset.value = currentY.value - startY.value
    return
  }

  const candidate = scrollDragCandidate.value
  if (!candidate || !scrollDragScroller.value || scrollDragScroller.value.scrollTop > 0) {
    resetScrollDragHandoff()
    return
  }

  const touch = event.touches[0]
  const deltaX = touch.clientX - candidate.startX
  const deltaY = touch.clientY - candidate.startY
  const distance = Math.hypot(deltaX, deltaY)
  if (distance < MOBILE_PANEL_TAP_DRAG_TOLERANCE) return

  if (deltaY <= 0 || Math.abs(deltaY) <= Math.abs(deltaX)) {
    resetScrollDragHandoff()
    return
  }

  event.preventDefault()
  event.stopPropagation()
  const visibleHeight = panelRef.value?.getBoundingClientRect().height
  dragStartHeight.value =
    isAnimating.value && visibleHeight && visibleHeight > 0 ? visibleHeight : panelHeight.value
  isDragging.value = true
  isAnimating.value = false
  dragMode.value = 'touch'
  startY.value = candidate.startY
  currentY.value = touch.clientY
  startTime.value = candidate.startTime
  dragOffset.value = deltaY
  scrollDragOwnsPanel.value = true
  suppressScrollContextMenuUntil = Date.now() + 750
  notifyMobilePanelDragStart()
}

const handleScrollTouchEnd = () => {
  if (scrollDragOwnsPanel.value && isDragging.value) finishPanelDrag()
  resetScrollDragHandoff()
}

const handleScrollTouchCancel = () => {
  if (scrollDragOwnsPanel.value && isDragging.value) finishPanelDrag()
  resetScrollDragHandoff()
}

const handleScrollContextMenu = (event: MouseEvent) => {
  if (!scrollDragOwnsPanel.value && Date.now() > suppressScrollContextMenuUntil) return
  event.preventDefault()
  event.stopPropagation()
}

const handleTouchEnd = () => {
  if (!isDragging.value) return
  finishPanelDrag()
}

// Mouse drag handlers (desktop)
const handleMouseDown = (event: MouseEvent) => {
  if (event.button !== 0) return // left button only

  // Only initiate drag from the handle area; the handler is attached there
  event.preventDefault()
  const visibleHeight = panelRef.value?.getBoundingClientRect().height
  dragStartHeight.value =
    isAnimating.value && visibleHeight && visibleHeight > 0 ? visibleHeight : panelHeight.value
  isDragging.value = true
  isAnimating.value = false
  dragMode.value = 'mouse'
  startY.value = event.clientY
  currentY.value = event.clientY
  startTime.value = Date.now()
  dragOffset.value = 0
  mouseDragged.value = false

  setupMouseListeners()
}

const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging.value || dragMode.value !== 'mouse') return
  event.preventDefault()
  currentY.value = event.clientY
  const deltaY = currentY.value - startY.value
  if (Math.abs(deltaY) > 3) mouseDragged.value = true
  dragOffset.value = deltaY
}

// Mouse listeners now handled through Vue composables
let removeMouseMoveListener: (() => void) | null = null
let removeMouseUpListener: (() => void) | null = null

const setupMouseListeners = () => {
  if (typeof window === 'undefined') return

  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', handleMouseUp)

  removeMouseMoveListener = () => window.removeEventListener('mousemove', handleMouseMove)
  removeMouseUpListener = () => window.removeEventListener('mouseup', handleMouseUp)
}

const cleanupMouseListeners = () => {
  if (removeMouseMoveListener) {
    removeMouseMoveListener()
    removeMouseMoveListener = null
  }
  if (removeMouseUpListener) {
    removeMouseUpListener()
    removeMouseUpListener = null
  }
}

const handleMouseUp = () => {
  if (!isDragging.value || dragMode.value !== 'mouse') {
    cleanupMouseListeners()
    dragMode.value = null
    return
  }

  finishPanelDrag()
  cleanupMouseListeners()
}

// Simple click handler for desktop toggle support
const handleDragHandleClick = (event: MouseEvent) => {
  // Prevent event from bubbling up
  event.preventDefault()
  event.stopPropagation()

  // Don't interfere with touch events (this handler is for mouse only)
  if (event.type === 'click' && 'ontouchstart' in window) {
    return
  }

  // If a drag just occurred, suppress the click toggle
  if (mouseDragged.value) {
    mouseDragged.value = false
    return
  }

  isAnimating.value = true

  if (isMobileBottomPanelExpanded.value) {
    // Currently expanded, so collapse
    panelHeightMode.value = 'collapsed'
    uiStore.collapseMobileBottomPanel()
  } else {
    // Currently collapsed, so expand
    panelHeightMode.value = 'expanded'
    uiStore.expandMobileBottomPanel()
  }

  // Reset animation state after transition
  resetAfterTransition()
}

// Navigation handlers
const handleWorkspaceSelect = async (workspaceId: string, workspaceName: string) => {
  if (!(await requestRouteModeLeave())) return

  selectedWorkspaceName.value = workspaceName

  if (workspaceId === '__all_activities__') {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
    currentView.value = 'all-activities'
    void router.push({ name: 'library' })
    return
  }

  // If coming from the Library view, collections are already temporarily hidden — skip saving
  // the current state to avoid corrupting the saved visibility with all-false.
  const cameFromLibrary =
    currentView.value === 'all-activities' || currentView.value === 'all-activities-tour-detail'
  currentView.value = 'workspace-detail'
  if (cameFromLibrary) {
    searchWorkspaceStore.hideAllWorkspacesTemporary()
  } else {
    searchWorkspaceStore.hideAllWorkspacesForNavigation()
  }
  uiStore.setWorkspacesOverview(false)
  uiStore.beginMapNavigation('collection-route')
  emit('select-workspace', workspaceId)
}

const enterPinnedPanel = (event?: Event) => {
  searchWorkspaceStore.hideAllWorkspacesTemporary()
  uiStore.setWorkspacesOverview(false)
  currentView.value = 'workspace-detail'
  selectedWorkspaceName.value = 'Pinned'
  const preserveMapNavigationIntent = (
    event as CustomEvent<{ preserveMapNavigationIntent?: boolean }> | undefined
  )?.detail?.preserveMapNavigationIntent
  if (!preserveMapNavigationIntent) {
    uiStore.beginMapNavigation('map')
  }
}

const handleBackToWorkspaces = async () => {
  if (currentView.value === 'all-activities-tour-detail') {
    mobileTourDetailTour.value = null
    currentView.value = 'all-activities'
    return
  }
  if (currentView.value === 'all-activities') {
    uiStore.hideTourDetailPanel()
    searchWorkspaceStore.activateAllWorkspaces()
    currentView.value = 'workspaces'
    uiStore.beginMapNavigation(null, { preserveMobilePanelState: true })
    void router.push({ name: 'workspaces' })
    return
  }

  if (!(await requestRouteModeLeave())) return

  // High is never transferred across panel surfaces. Capture the normalized destination snap
  // before clearing the collection store, then reapply after the async overview load so route/store
  // watcher ordering cannot overwrite it.
  const overviewPanelMode: PanelHeightMode =
    panelHeightMode.value === 'collapsed' ? 'collapsed' : 'expanded'

  // Signal the clientId watcher that this transition is toolbar-initiated so the overview entry
  // preserves the drawer's current collapsed/expanded state instead of forcing a reset.
  isToolbarNavigatingToOverview.value = true
  uiStore.beginMapNavigation('back')
  workspaceStore.clearToOverviewState()
  uiStore.setWorkspacesOverview(true)

  isWorkspaceInfoSheetOpen.value = false

  // Initialize and load collections when going back to overview
  await searchWorkspaceStore.init()
  if (authStore.isAuthenticated) {
    try {
      const collections = await searchWorkspaceStore.ensureWorkspacesLoaded(true)
      searchWorkspaceStore.setAllWorkspaces(collections)
      searchWorkspaceStore.activateAllWorkspaces()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.UI, 'Failed to load collections in handleBackToWorkspaces', {
        error,
      })
    }
  }

  animatePanelToMode(overviewPanelMode)

  router.push({ name: 'workspaces' })
}

// Which destination the persistent bottom tab bar should highlight. The "+" (create)
// and Profile slots are never "active" — they are an action menu and a menu trigger.
const activeMobileTab = computed<'collections' | 'library' | 'pinned' | 'search' | null>(() => {
  if (uiStore.isSmartSearchOpen) return 'search'
  if (route.name === 'pinned') return 'pinned'
  // Bare map view is the neutral "no surface open" state (app launch, or after dismissing a
  // drawer): no tab should be highlighted. A tab is green only when its surface is visible.
  if (uiStore.isMobileMapView) return null
  switch (currentView.value) {
    case 'workspaces':
    case 'workspace-detail':
      return 'collections'
    case 'all-activities':
    case 'all-activities-tour-detail':
      return 'library'
    default:
      return 'collections'
  }
})

// "Map" tab — the default mobile state: hide the drawer and show only the map.
const enterMobileMapView = () => {
  showDropdownMenu.value = false
  uiStore.setMobileMapView(true)
}

const openMobileCampTab = async () => {
  if (!(await requestRouteModeLeave())) return

  uiStore.setMobileMapView(false)
  // Opening the Collections surface fits the map to the visible collections; only the
  // drawer's expansion state is preserved across the switch.
  uiStore.beginMapNavigation(null, { preserveMobilePanelState: true })
  uiStore.enableAutoViewportAdaptation()
  libraryStore.exitSelectionMode()
  mobileTourDetailTour.value = null
  mobileDetail.value = null
  uiStore.hideTourDetailPanel()
  isWorkspaceInfoSheetOpen.value = false
  showDropdownMenu.value = false
  workspaceStore.clearToOverviewState()
  uiStore.setWorkspacesOverview(true)
  searchWorkspaceStore.activateAllWorkspaces()
  currentView.value = 'workspaces'
}

// Opens the Library ("My Activities & Routes") destination from the bottom tab bar.
// Mirrors the `open-all-activities` window-event listener so behaviour is identical
// whether entered from the tab bar or from the CreateMenu library actions.
const openMobileLibraryTab = async () => {
  if (!(await requestRouteModeLeave())) return

  uiStore.setMobileMapView(false)
  if (currentView.value === 'all-activities') {
    return
  }

  // Opening the Library fits the map to all tours: LibraryTourLayer's mount zoom is gated
  // on auto viewport adaptation, so re-enable it for this navigation context change.
  uiStore.beginMapNavigation(null, { preserveMobilePanelState: true })
  uiStore.enableAutoViewportAdaptation()
  libraryStore.exitSelectionMode()
  showDropdownMenu.value = false
  isWorkspaceInfoSheetOpen.value = false
  mobileTourDetailTour.value = null
  mobileDetail.value = null
  uiStore.hideTourDetailPanel()
  searchWorkspaceStore.hideAllWorkspacesTemporary()
  currentView.value = 'all-activities'
}

// Exposed so the persistent MobileTabBar (rendered as a sibling in
// WorkspaceMapContainer) can drive navigation and read the active destination.
defineExpose({
  activeMobileTab,
  openMobileCampTab,
  openMobileLibraryTab,
  openMobilePinnedTab: enterPinnedPanel,
  enterMobileMapView,
  handleBackToWorkspaces,
})

// A collection detail / library view always shows the drawer — leaving map view. Runs
// immediately so deep links into a collection open with the drawer visible.
watch(
  () => currentView.value,
  (view) => {
    if (view !== 'workspaces') uiStore.setMobileMapView(false)
  },
  { immediate: true },
)

// Recompute the published map inset whenever we enter/leave map view (and on load).
watch(
  () => uiStore.isMobileMapView,
  () => scheduleMeasuredMobileBottomPanelHeightPublish(),
  { immediate: true },
)

// Event handlers for various actions
const handleViewPhoto = (photoId: string) => {
  emit('view-photo', photoId)
}

const handleViewCoverPhoto = () => {
  if (!workspaceStore.coverPhotoClientId) return
  handleViewPhoto(workspaceStore.coverPhotoClientId)
}

const handlePhotoZoom = (photoId: string) => {
  emit('zoom-photo', photoId)
}

const handleOpenPOIDetail = (poiId: string) => {
  emit('open-poi-detail', poiId)
}

const handleZoomPOI = (poiId: string) => {
  emit('zoom-poi', poiId)
}

const canAddCollectionContent = computed(() => hasPermission('edit_track'))
const canAddPOIToCollection = computed(() => hasPermission('edit_waypoint'))

const handleTogglePOIsVisibility = () => {
  uiStore.setPOIsVisible(!uiStore.arePOIsVisible)
}

// Find earliest start time for a tour (ms since epoch)
const getTourStartTime = (tourId: string): number | null => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return null
  const times = tour.tracks
    .map((t) => t.startTime)
    .filter((v): v is number => typeof v === 'number' && !isNaN(v))
  if (times.length === 0) return null
  return Math.min(...times)
}

// Find photoId with takenAt time nearest to given timestamp
const findNearestPhotoIdByTime = (timestamp: number): string | null => {
  const list = photosSortedByDate?.value ?? []
  // Consider only photos with a takenAt timestamp
  const timed = list.filter((p) => typeof p.takenAt === 'number')
  if (timed.length === 0) return null
  let best: { id: string; diff: number } | null = null
  for (const p of timed) {
    const diff = Math.abs((p.takenAt as number) - timestamp)
    if (!best || diff < best.diff) {
      best = { id: p.clientId, diff }
    }
  }
  return best ? best.id : null
}

// Get the tour start location (lat/lon) by using the earliest-starting track if available,
// or falling back to the first available point
const getTourStartLocation = (tourId: string): { lat: number; lon: number } | null => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return null
  if (!tour.tracks || tour.tracks.length === 0) return null

  const tracks = tour.tracks.slice()
  tracks.sort((a, b) => {
    const aTime = typeof a.startTime === 'number' ? a.startTime : Number.POSITIVE_INFINITY
    const bTime = typeof b.startTime === 'number' ? b.startTime : Number.POSITIVE_INFINITY
    return aTime - bTime
  })

  for (const track of tracks) {
    for (const segment of track.segments ?? []) {
      const first = segment.points?.[0]
      if (first && typeof first.lat === 'number' && typeof first.lon === 'number') {
        return { lat: first.lat, lon: first.lon }
      }
    }
    // Fallback: parse reduced LINESTRING coordinates from API and take first point
    const lineString = getBestTrackLineString(track)
    if (lineString) {
      const coords = parseLineString(lineString)
      if (coords.length > 0) {
        const [lng, lat] = coords[0]
        if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lon: lng }
        }
      }
    }
  }
  return null
}

// Find nearest photo by geographic distance to a given location
const findNearestPhotoIdByLocation = (lat: number, lon: number): string | null => {
  const list = photosSortedByDate?.value ?? []
  const withCoords = list.filter(
    (p) => p.hasCoordinates && typeof p.lat === 'number' && typeof p.lon === 'number',
  )
  if (withCoords.length === 0) {
    return null
  }
  let best: { id: string; dist: number } | null = null
  for (const p of withCoords) {
    const d = calculateHaversineDistance(lat, lon, p.lat as number, p.lon as number)
    if (!best || d < best.dist) best = { id: p.clientId, dist: d }
  }
  return best ? best.id : null
}

// Smoothly scroll the photos list so the given photo tile is visible below the sticky header
const scrollPhotosToPhoto = (photoId: string) => {
  const container = scrollContainer.value
  if (!container) {
    return
  }
  // Photo tiles are marked with data-photo-id on the outer tile div
  const el = container.querySelector(`[data-photo-id="${photoId}"]`) as HTMLElement | null
  if (!el) {
    return
  }
  const header = photosHeaderRef.value
  const headerHeight = header ? header.offsetHeight : 0
  const cRect = container.getBoundingClientRect()
  const eRect = el.getBoundingClientRect()
  const targetTop = container.scrollTop + (eRect.top - cRect.top) - headerHeight - 8

  container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
}

const handleZoomTour = (tourId: string) => {
  emit('zoom-tour', tourId)
}

const handleSelectTour = (tourId: string) => {
  emit('select-tour', tourId)
}

const handleEnterTrackEdit = (tourId: string, trackClientId: string) => {
  emit('enter-track-edit', tourId, trackClientId)
}

const scrollToTours = () => {
  const container = scrollContainer.value
  if (container) {
    container.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const handleToggleAllToursVisibility = async () => {
  if (!hasTours.value) return
  await workspaceStore.setAllToursVisibility(!isAnyTourVisible.value)
}

const handleTogglePhotosVisibility = () => {
  uiStore.setPhotosVisible(!arePhotosVisible.value)
}

const handleAddEmptyTour = async () => {
  if (!(await requirePermission('edit_tour'))) return
  showDropdownMenu.value = false
  emit('create-tour')
}

const handleCreateRouteFromModal = async ({ type }: { type?: string }) => {
  isCreateTourModalOpen.value = false
  try {
    const tourId = await libraryStore.createEmptyTour({ type })
    if (!tourId) return
    await nextTick()
    window.dispatchEvent(
      new CustomEvent('start-track-drawing', {
        detail: { tourId, scope: 'library', discardTourOnCancel: true },
      }),
    )
  } catch (error) {
    logService.error(LOG_CATEGORIES.UI, 'Failed to create route', { error })
  }
}

const handleDrawRouteIntoCollection = async () => {
  if (!(await requirePermission('edit_track'))) return
  const tourId = workspaceStore.addLocalDraftTour()
  await nextTick()
  window.dispatchEvent(
    new CustomEvent('start-track-drawing', {
      detail: { tourId, scope: 'workspace', discardTourOnCancel: true },
    }),
  )
}

const scrollToSection = (element: HTMLElement | null) => {
  const container = scrollContainer.value
  if (!container || !element) return
  const cRect = container.getBoundingClientRect()
  const eRect = element.getBoundingClientRect()
  container.scrollTo({
    top: Math.max(0, container.scrollTop + eRect.top - cRect.top),
    behavior: 'smooth',
  })
}

const scrollToPhotos = () => {
  scrollToSection(photosHeaderRef.value ?? photosSectionRef.value)
}

const scrollToPOIs = () => {
  scrollToSection(poiHeaderRef.value)
}

const recomputePhotosHeaderVisibility = () => {
  const container = scrollContainer.value
  if (!container || currentView.value !== 'workspace-detail') {
    isPhotosHeaderVisible.value = false
    isPOIHeaderVisible.value = false
    return
  }

  const containerRect = container.getBoundingClientRect()
  const isInView = (element: HTMLElement | null) => {
    if (!element) return false
    const rect = element.getBoundingClientRect()
    return rect.bottom > containerRect.top && rect.top < containerRect.bottom
  }
  // Both the Points of Interest and Photos headers get a pinned quick-access footer when their
  // in-flow header scrolls out of view, so the two sections behave the same on scroll.
  isPhotosHeaderVisible.value = isInView(photosHeaderRef.value ?? photosSectionRef.value)
  isPOIHeaderVisible.value = isInView(poiHeaderRef.value)
}

const schedulePhotosHeaderVisibility = () => {
  nextTick(() => requestAnimationFrame(recomputePhotosHeaderVisibility))
}

const showPinnedPhotosHeader = computed(
  () =>
    currentView.value === 'workspace-detail' &&
    !isDetailLoading.value &&
    (hasPhotos.value || hasTours.value) &&
    !isPhotosHeaderVisible.value,
)

const showPinnedPOIHeader = computed(
  () =>
    currentView.value === 'workspace-detail' &&
    !isDetailLoading.value &&
    (hasPOIs.value || hasTours.value) &&
    !isPOIHeaderVisible.value,
)

const triggerGpxUpload = () => {
  emit('trigger-gpx-upload')
}

const triggerPhotoUpload = () => {
  emit('trigger-photo-upload')
}

const triggerMixedUpload = () => {
  emit('trigger-mixed-upload')
}

const closeUploadDialog = () => {
  isUploadDialogOpen.value = false
  uiStore.setDialogOpen(false)
}

const closeOfflineDialog = () => {
  isOfflineDialogOpen.value = false
  if (!activeOfflineDownload.value) {
    showDropdownMenu.value = false
  }
  uiStore.setDialogOpen(false)
}

watch(activeOfflineDownload, (download, previousDownload) => {
  if (!isOfflineDialogOpen.value) return
  if (previousDownload || !download || download.error) return

  isOfflineDialogOpen.value = false
  uiStore.setDialogOpen(false)
})

const handleUploadDialogGpx = () => {
  triggerGpxUpload()
}

const handleUploadDialogPhoto = () => {
  triggerPhotoUpload()
}
const allowPhotoUploadInDialog = computed(() => currentView.value === 'workspace-detail')

const requestCollectionDrawRoute = async () => {
  if (currentView.value !== 'workspace-detail') return
  await handleDrawRouteIntoCollection()
}

const requestCollectionImportFiles = () => {
  if (currentView.value !== 'workspace-detail') return
  triggerMixedUpload()
}

const requestCollectionPOI = () => {
  if (currentView.value !== 'workspace-detail') return
  window.dispatchEvent(new CustomEvent('open-add-poi-modal'))
}

const navigatePinnedEmptyStateToLibrary = () => {
  void router.push({ name: 'library' })
}

const navigatePinnedEmptyStateToCollections = () => {
  void router.push({ name: 'workspaces' })
}

useEventListener(window, 'workspace-add-draw-route', () => {
  void requestCollectionDrawRoute()
})
useEventListener(window, 'workspace-add-import-files', requestCollectionImportFiles)
useEventListener(window, 'workspace-add-poi', requestCollectionPOI)
useEventListener(window, 'workspace-mobile-panel-request-expanded', () => {
  if (panelHeightMode.value !== 'high') return
  animatePanelToMode('expanded')
})

const closeShareModal = () => {
  isShareModalOpen.value = false
  uiStore.setDialogOpen(false)
}

const openShareModal = () => {
  isShareModalOpen.value = true
}

watch(
  () => uiStore.pendingWorkspaceShareOpen,
  (pending) => {
    if (!pending) return
    uiStore.consumeWorkspaceShareOpen()
    openShareModal()
  },
  { immediate: true },
)

// Deep link focus handler: scroll photos to tour start without map zoom
const handleFocusTourEvent = (evt: Event) => {
  const detail = (evt as CustomEvent).detail as { tourId?: string }
  const tourId = detail?.tourId
  if (!tourId) return
  if (currentView.value !== 'workspace-detail' || !hasPhotos.value) return

  const start = getTourStartTime(tourId)
  let targetPhotoId: string | null = null
  if (start) targetPhotoId = findNearestPhotoIdByTime(start)
  if (!targetPhotoId) {
    const loc = getTourStartLocation(tourId)
    if (loc) targetPhotoId = findNearestPhotoIdByLocation(loc.lat, loc.lon)
  }
  if (!targetPhotoId) return
  nextTick(() => requestAnimationFrame(() => scrollPhotosToPhoto(targetPhotoId!)))
}

useEventListener(window, 'focus-tour', handleFocusTourEvent)
useEventListener(window, 'open-pinned-surface', enterPinnedPanel)
useEventListener(window, 'open-all-activities', (event: Event) => {
  if (currentView.value === 'all-activities') return
  const detail = (event as CustomEvent<{ preserveMobilePanelState?: boolean }>).detail

  mobileTourDetailTour.value = null
  searchWorkspaceStore.hideAllWorkspacesTemporary()
  currentView.value = 'all-activities'
  if (!detail?.preserveMobilePanelState) {
    uiStore.expandMobileBottomPanel()
  }
})

// Collection detail editing methods
const startEditingName = async () => {
  if (workspaceStore.isSystem) return
  if (!(await requirePermission('rename_workspace'))) return
  openMobileRenameSheet({
    title: 'Rename collection',
    currentName: workspaceStore.name,
    submit: async (name) => {
      if (name === workspaceStore.name) return
      await workspaceStore.updateWorkspace({ name })
    },
  })
}

// Computed properties for deletion state
const daysUntilDeletion = computed(() => {
  return getDaysUntilDeletion(workspaceStore.deletionScheduledAt)
})

const canDelete = computed(() => {
  return (
    !workspaceStore.isSystem &&
    hasPermission('delete_workspace') &&
    !workspaceStore.markedForDeletion
  )
})

const canRestore = computed(() => {
  return (
    !workspaceStore.isSystem &&
    hasPermission('delete_workspace') &&
    workspaceStore.markedForDeletion
  )
})

// An invited member (Collaborator/Viewer) can leave; owners delete instead.
const canLeave = computed(() => {
  return !workspaceStore.isOwner && !workspaceStore.markedForDeletion
})

// Delete/restore methods
const handleDeleteClick = async () => {
  if (!(await requirePermission('delete_workspace'))) return
  const confirmed = await showConfirm({
    title: 'Delete collection?',
    message: `Are you sure you want to delete "${workspaceStore.name}"? \n\n The collection will be marked for deletion and permanently removed after 7 days.\n\nOnly owners can access the workspace during this period. You won't find it in search, but it can be restored if you know the link.`,
    confirmText: 'Delete collection',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    try {
      await workspaceStore.markWorkspaceForDeletion()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.UI, 'markWorkspaceForDeletion failed (handled in store)', {
        error,
      })
    }
  }
}

const handleRestoreClick = async () => {
  if (!(await requirePermission('delete_workspace'))) return
  try {
    await workspaceStore.restoreWorkspace()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'restoreWorkspace failed (handled in store)', { error })
  }
}

const handleLeaveClick = async () => {
  showDropdownMenu.value = false
  const confirmed = await showConfirm({
    title: 'Leave collection?',
    message: `Are you sure you want to leave "${workspaceStore.name}"?\n\nYou will lose access to this collection. You can only rejoin if you're invited again.`,
    confirmText: 'Leave collection',
    cancelText: 'Cancel',
    variant: 'danger',
  })
  if (!confirmed) return
  try {
    await workspaceStore.leaveWorkspace()
    await handleBackToWorkspaces()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'leaveWorkspace failed (handled in store)', { error })
  }
}

const handleReloadCurrentWorkspace = async () => {
  const workspaceId = routeWorkspaceId.value
  if (!workspaceId) return

  showDropdownMenu.value = false
  uiStore.enableAutoViewportAdaptation()

  try {
    await syncService.loadRemoteWorkspace(workspaceId, false)
  } finally {
    uiStore.setWorkspacesOverview(false)
    window.dispatchEvent(new CustomEvent('zoom-to-content'))
  }
}

const resetWorkspaceInfoEdits = () => {
  isWorkspaceInfoNameEditing.value = false
  workspaceInfoNameDraft.value = ''
  workspaceInfoNameError.value = ''
  isWorkspaceInfoNameSaving.value = false
  isWorkspaceInfoDescriptionEditing.value = false
  workspaceInfoDescriptionDraft.value = ''
  workspaceInfoDescriptionError.value = ''
  isWorkspaceInfoDescriptionSaving.value = false
}

const closeWorkspaceInfoSheet = () => {
  if (isWorkspaceInfoNameSaving.value || isWorkspaceInfoDescriptionSaving.value) return
  isWorkspaceInfoSheetOpen.value = false
  resetWorkspaceInfoEdits()
}

const loadWorkspaceInfoMembers = async () => {
  const requestId = ++workspaceInfoMembersRequestId
  workspaceInfoMembers.value = []
  if (
    workspaceStore.isPublic ||
    !workspaceStore.clientId ||
    !workspaceStore.isPersistedToServer ||
    !workspaceStore.isOwner
  ) {
    isWorkspaceInfoMembersLoading.value = false
    return
  }

  isWorkspaceInfoMembersLoading.value = true
  try {
    const members = await apiService.getWorkspaceMembers(workspaceStore.clientId, {
      suppressToast: true,
    })
    if (requestId === workspaceInfoMembersRequestId) workspaceInfoMembers.value = members
  } catch (error) {
    logService.warn(LOG_CATEGORIES.API, 'Failed to load collection members for info sheet', {
      error,
      workspaceId: workspaceStore.clientId,
    })
  } finally {
    if (requestId === workspaceInfoMembersRequestId) isWorkspaceInfoMembersLoading.value = false
  }
}

const focusWorkspaceInfoNameInput = async () => {
  await nextTick()
  const input = document.getElementById('workspace-info-name')
  if (input instanceof HTMLInputElement) input.focus({ preventScroll: true })
}

const startWorkspaceInfoNameEdit = async () => {
  if (isWorkspaceInfoDescriptionSaving.value) return
  if (!(await requirePermission('rename_workspace'))) return
  if (isWorkspaceInfoDescriptionSaving.value) return
  cancelWorkspaceInfoDescriptionEdit()
  workspaceInfoNameDraft.value = workspaceStore.name
  workspaceInfoNameError.value = ''
  isWorkspaceInfoNameEditing.value = true
  void focusWorkspaceInfoNameInput()
}

const cancelWorkspaceInfoNameEdit = () => {
  if (isWorkspaceInfoNameSaving.value) return
  isWorkspaceInfoNameEditing.value = false
  workspaceInfoNameDraft.value = ''
  workspaceInfoNameError.value = ''
}

const saveWorkspaceInfoName = async () => {
  if (!isWorkspaceInfoNameEditing.value || isWorkspaceInfoNameSaving.value) return
  const name = workspaceInfoNameDraft.value.trim()
  if (!name) {
    workspaceInfoNameError.value = 'Name cannot be empty'
    void focusWorkspaceInfoNameInput()
    return
  }
  if (name.length > 255) {
    workspaceInfoNameError.value = 'Name must be 255 characters or fewer'
    void focusWorkspaceInfoNameInput()
    return
  }
  if (name === workspaceStore.name) {
    cancelWorkspaceInfoNameEdit()
    return
  }
  if (!(await requirePermission('rename_workspace'))) return

  workspaceInfoNameError.value = ''
  isWorkspaceInfoNameSaving.value = true
  try {
    await workspaceStore.updateWorkspace({ name })
    isWorkspaceInfoNameEditing.value = false
    workspaceInfoNameDraft.value = ''
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to rename collection from collection info', {
      error,
    })
    workspaceInfoNameError.value = `Failed to rename: ${error instanceof Error ? error.message : String(error)}`
  } finally {
    isWorkspaceInfoNameSaving.value = false
  }
}

const handleWorkspaceInfoHeaderLeft = () => {
  if (isWorkspaceInfoNameEditing.value) {
    cancelWorkspaceInfoNameEdit()
    return
  }
  closeWorkspaceInfoSheet()
}

const resizeWorkspaceInfoDescription = (event?: Event) => {
  if (event) workspaceInfoDescriptionError.value = ''
  const textarea =
    event?.target instanceof HTMLTextAreaElement
      ? event.target
      : document.getElementById('workspace-info-description')
  if (!(textarea instanceof HTMLTextAreaElement)) return
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

let workspaceInfoDescriptionLastTouchY: number | null = null
let workspaceInfoDescriptionParentScroll: HTMLElement | null = null
let workspaceInfoDescriptionParentScrollTop = 0
let workspaceInfoDescriptionParentScrollFrame: number | null = null

const stopWorkspaceInfoDescriptionParentScrollLock = () => {
  if (workspaceInfoDescriptionParentScrollFrame !== null) {
    cancelAnimationFrame(workspaceInfoDescriptionParentScrollFrame)
    workspaceInfoDescriptionParentScrollFrame = null
  }
  if (workspaceInfoDescriptionParentScroll) {
    workspaceInfoDescriptionParentScroll.scrollTop = workspaceInfoDescriptionParentScrollTop
    workspaceInfoDescriptionParentScroll = null
  }
}

const startWorkspaceInfoDescriptionParentScrollLock = (textarea: HTMLTextAreaElement) => {
  stopWorkspaceInfoDescriptionParentScrollLock()
  const parentScroll = textarea.closest('[data-mobile-action-sheet-scroll]')
  if (!(parentScroll instanceof HTMLElement)) return

  workspaceInfoDescriptionParentScroll = parentScroll
  workspaceInfoDescriptionParentScrollTop = parentScroll.scrollTop
  const maintainParentScroll = () => {
    const activeParentScroll = workspaceInfoDescriptionParentScroll
    if (!activeParentScroll) {
      workspaceInfoDescriptionParentScrollFrame = null
      return
    }
    activeParentScroll.scrollTop = workspaceInfoDescriptionParentScrollTop
    workspaceInfoDescriptionParentScrollFrame = requestAnimationFrame(maintainParentScroll)
  }
  workspaceInfoDescriptionParentScrollFrame = requestAnimationFrame(maintainParentScroll)
}

const handleWorkspaceInfoDescriptionTouchStart = (event: TouchEvent) => {
  event.stopPropagation()
  const textarea = event.target
  if (textarea instanceof HTMLTextAreaElement) {
    startWorkspaceInfoDescriptionParentScrollLock(textarea)
  }
  workspaceInfoDescriptionLastTouchY = event.touches.length === 1 ? event.touches[0].clientY : null
}

const handleWorkspaceInfoDescriptionTouchMove = (event: TouchEvent) => {
  event.stopPropagation()
  if (event.touches.length !== 1 || workspaceInfoDescriptionLastTouchY === null) return
  const textarea = event.target
  if (!(textarea instanceof HTMLTextAreaElement)) return

  const currentTouchY = event.touches[0].clientY
  const movingPastTop =
    currentTouchY > workspaceInfoDescriptionLastTouchY && textarea.scrollTop <= 0
  const movingPastBottom =
    currentTouchY < workspaceInfoDescriptionLastTouchY &&
    textarea.scrollTop + textarea.clientHeight >= textarea.scrollHeight - 1

  if ((movingPastTop || movingPastBottom) && event.cancelable) event.preventDefault()
  workspaceInfoDescriptionLastTouchY = currentTouchY
}

const handleWorkspaceInfoDescriptionTouchEnd = (event: TouchEvent) => {
  event.stopPropagation()
  workspaceInfoDescriptionLastTouchY = null
  stopWorkspaceInfoDescriptionParentScrollLock()
}

const focusWorkspaceInfoDescriptionInput = async () => {
  await nextTick()
  resizeWorkspaceInfoDescription()
  const textarea = document.getElementById('workspace-info-description')
  if (textarea instanceof HTMLTextAreaElement) textarea.focus({ preventScroll: true })
}

const startWorkspaceInfoDescriptionEdit = async () => {
  if (isWorkspaceInfoNameSaving.value) return
  if (!(await requirePermission('edit_workspace_description'))) return
  if (isWorkspaceInfoNameSaving.value) return
  cancelWorkspaceInfoNameEdit()
  workspaceInfoDescriptionDraft.value = workspaceStore.description ?? ''
  workspaceInfoDescriptionError.value = ''
  isWorkspaceInfoDescriptionEditing.value = true
  void focusWorkspaceInfoDescriptionInput()
}

const cancelWorkspaceInfoDescriptionEdit = () => {
  if (isWorkspaceInfoDescriptionSaving.value) return
  isWorkspaceInfoDescriptionEditing.value = false
  workspaceInfoDescriptionDraft.value = ''
  workspaceInfoDescriptionError.value = ''
}

const saveWorkspaceInfoDescription = async () => {
  if (!isWorkspaceInfoDescriptionEditing.value || isWorkspaceInfoDescriptionSaving.value) return
  if (workspaceInfoDescriptionDraft.value.length > 2048) {
    workspaceInfoDescriptionError.value = 'Description must be 2048 characters or fewer'
    void focusWorkspaceInfoDescriptionInput()
    return
  }
  if (workspaceInfoDescriptionDraft.value === (workspaceStore.description ?? '')) {
    cancelWorkspaceInfoDescriptionEdit()
    return
  }
  if (!(await requirePermission('edit_workspace_description'))) return

  workspaceInfoDescriptionError.value = ''
  isWorkspaceInfoDescriptionSaving.value = true
  try {
    await workspaceStore.updateWorkspace({ description: workspaceInfoDescriptionDraft.value })
    isWorkspaceInfoDescriptionEditing.value = false
    workspaceInfoDescriptionDraft.value = ''
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update description from collection info', {
      error,
    })
    workspaceInfoDescriptionError.value = `Failed to save: ${error instanceof Error ? error.message : String(error)}`
  } finally {
    isWorkspaceInfoDescriptionSaving.value = false
  }
}

const openWorkspaceInfoSheet = () => {
  showDropdownMenu.value = false
  resetWorkspaceInfoEdits()
  const rect = panelRef.value?.getBoundingClientRect()
  const renderedHeight = rect?.height ?? 0
  const bottomGap =
    rect && typeof window !== 'undefined' ? Math.max(0, window.innerHeight - rect.bottom) : 0
  const renderedHighHeight =
    typeof window === 'undefined'
      ? highPanelHeight.value
      : Math.min(
          highPanelHeight.value,
          window.innerHeight - readSafeAreaInsetTopPx() - 8 - bottomGap,
        )

  const expandedCardHeight =
    panelHeightMode.value === 'expanded' && renderedHeight > 0
      ? renderedHeight
      : maxPanelHeight.value
  const highCardHeight =
    panelHeightMode.value === 'high' && renderedHeight > 0 ? renderedHeight : renderedHighHeight

  // MobileActionSheet is anchored to the viewport bottom and covers the tab bar, while the
  // collection panel floats above it. Add the panel's live bottom gap so equal values describe
  // equal top edges rather than merely equal card heights.
  workspaceInfoSheetExpandedHeight.value = Math.round(expandedCardHeight + bottomGap)
  workspaceInfoSheetHighHeight.value = Math.round(highCardHeight + bottomGap)
  workspaceInfoSheetInitialSnap.value = panelHeightMode.value === 'high' ? 'high' : 'medium'
  isWorkspaceInfoSheetOpen.value = true
  void loadWorkspaceInfoMembers()
}

// Expand toolbar when workspace name or "My Collections" is clicked while collapsed
const handleHeaderClick = async () => {
  // When collapsed: expand with animation.
  // Set isAnimating first and wait for the browser to paint the transition style
  // before changing the height — otherwise Vue batches both into one DOM update
  // and the transition has nothing to animate from.
  if (!isMobileBottomPanelExpanded.value) {
    isAnimating.value = true
    await nextTick()
    requestAnimationFrame(() => {
      uiStore.expandMobileBottomPanel()
      // Do NOT call resetAfterTransition() here — that would set isAnimating=false
      // in the next frame, stripping the transition style before it finishes.
      // The @transitionend handler (handleMobileToolbarTransitionEnd) resets it.
    })
    return
  }

  // When expanded in collections overview: zoom to visible collections
  if (currentView.value === 'workspaces') {
    handleZoomToCollections()
    return
  }

  // When expanded in collection detail: reload current collection and reset map
  if (currentView.value === 'workspace-detail') {
    await handleReloadCurrentWorkspace()
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  if (isOfflineDialogOpen.value) return

  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showDropdownMenu.value = false
  }
}

// Use Vue composable for global event listeners
useEventListener(window, 'resize', resizeHandler)
useEventListener(window, 'resize', schedulePhotosHeaderVisibility)
useEventListener(window, 'mobile-bottom-panel-expanded-height', () => {
  animatePanelToMode('expanded')
})

// Close tour action bar menus when clicking outside
useEventListener(document, 'click', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-tour-sort-menu]')) closeTourSortMenu()
  if (!target?.closest('[data-tour-upload-menu]')) closeTourUploadMenu()
  if (
    !target?.closest('[data-tour-actions-row]') &&
    tourSearchExpanded.value &&
    !tourSearchQuery.value
  ) {
    closeTourSearch()
  }
})

// Set up conditional event listeners for document clicks
let removeDocumentClickListener: (() => void) | null = null

// Initialize view state based on current workspace
onMounted(async () => {
  // Add click outside listener for dropdown
  if (typeof document !== 'undefined') {
    document.addEventListener('click', handleClickOutside)
    removeDocumentClickListener = () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }

  // Touch-specific viewport handling (replaces iOS-only logic)
  if (isTouch) {
    // Add viewport meta tag handling for edge insets on mobile UAs
    const metaViewport = document.querySelector('meta[name=viewport]')
    if (metaViewport) {
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, viewport-fit=cover',
      )
    }

    // Use composable for orientation change events
    useEventListener(window, 'orientationchange', () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resizeHandler)
      })
    })
  }

  // Use route params (not the store clientId) to decide the initial view — the store's
  // clientId is always truthy on first load (generateId() in state initializer), so it
  // can't reliably distinguish "workspace loaded" from "fresh store". The URL is the
  // authoritative source of truth.
  const routeWorkspaceIdOnMount =
    typeof route.params.workspaceId === 'string' && route.params.workspaceId
      ? route.params.workspaceId
      : null

  if (routeWorkspaceIdOnMount || isPinnedRoute()) {
    currentView.value = 'workspace-detail'
    selectedWorkspaceName.value = isPinnedRoute()
      ? 'Pinned'
      : workspaceStore.name || 'Untitled Collection'
    uiStore.setWorkspacesOverview(false)
    // Expand by default when a collection is loaded and the mobile toolbar is shown
    uiStore.expandMobileBottomPanel()
  } else {
    currentView.value = 'workspaces'
    uiStore.setWorkspacesOverview(true)
    // Fresh overview entry starts collapsed; back-navigation preserves the drawer's current state.
    if (!uiStore.consumeMobileBottomPanelStatePreservationOnOverviewEntry()) {
      uiStore.collapseMobileBottomPanel()
    }

    // Always initialize search collection store when in collections overview mode
    // This ensures collections are visible on the map even when mobile panel is collapsed
    await searchWorkspaceStore.init()
    if (authStore.isAuthenticated) {
      try {
        const collections = await searchWorkspaceStore.ensureWorkspacesLoaded(true)
        searchWorkspaceStore.setAllWorkspaces(collections)
        searchWorkspaceStore.activateAllWorkspaces()
      } catch (error) {
        logService.warn(LOG_CATEGORIES.UI, 'Failed to load collections in mobile toolbar', {
          error,
        })
      }
    }

    if (searchWorkspaceStore.hasSelectedWorkspaces) {
      searchWorkspaceStore.restoreWorkspaceVisibility()
    }
  }
  schedulePhotosHeaderVisibility()
}) // Watch for workspace changes
watch(
  () => workspaceStore.clientId,
  async (newClientId) => {
    if (newClientId) {
      currentView.value = 'workspace-detail'
      selectedWorkspaceName.value = workspaceStore.name || 'Untitled Collection'
      uiStore.setWorkspacesOverview(false)
      isWorkspaceInfoSheetOpen.value = false
      // Arm the one-shot "open tall when empty" check for this collection entry; it is
      // evaluated once the collection finishes loading (see the isDetailLoading watcher below).
      tallOpenEvaluatedClientId.value = null
    } else {
      currentView.value = 'workspaces'
      selectedWorkspaceName.value = ''
      uiStore.setWorkspacesOverview(true)
      isWorkspaceInfoSheetOpen.value = false
      // Fresh overview entry collapses the drawer; back-navigation preserves the user's current
      // collapsed/expanded state, whether triggered by the floating button or browser/native back.
      const shouldPreserveBottomPanelState =
        uiStore.consumeMobileBottomPanelStatePreservationOnOverviewEntry()
      if (!isToolbarNavigatingToOverview.value && !shouldPreserveBottomPanelState) {
        uiStore.collapseMobileBottomPanel()
      }
      isToolbarNavigatingToOverview.value = false

      // Initialize search collection store when switching back to collections overview
      await searchWorkspaceStore.init()
      if (authStore.isAuthenticated) {
        try {
          const collections = await searchWorkspaceStore.ensureWorkspacesLoaded(true)
          searchWorkspaceStore.setAllWorkspaces(collections)
          searchWorkspaceStore.activateAllWorkspaces()
        } catch (error) {
          logService.warn(LOG_CATEGORIES.UI, 'Failed to load collections in workspace watcher', {
            error,
          })
        }
      }
    }
  },
)

watch(
  () => workspaceStore.name,
  (newName) => {
    if (currentView.value === 'workspace-detail') {
      selectedWorkspaceName.value = newName || 'Untitled Collection'
    }
  },
)

// Open the mobile drawer tall (high snap) when entering an EMPTY collection, so the empty-state
// cards (Import / Draw route) are fully visible instead of clipped at the medium snap. One-shot
// per collection entry (guarded by tallOpenEvaluatedClientId) and evaluated only once the
// collection has loaded — so a still-loading populated collection isn't transiently treated as
// empty, and dragging down / adding the first item never re-snaps the drawer.
watch(
  () => isDetailLoading.value,
  (loading) => {
    if (loading) return
    if (currentView.value !== 'workspace-detail') return
    const clientId = workspaceStore.clientId
    if (!clientId || clientId !== routeWorkspaceId.value) return
    if (tallOpenEvaluatedClientId.value === clientId) return
    tallOpenEvaluatedClientId.value = clientId

    if (!hasTours.value && canAddCollectionContent.value) {
      // Set 'high' before expanding: the isMobileBottomPanelExpanded watcher only promotes
      // 'collapsed' → 'expanded', so it preserves a pre-set 'high'.
      animatePanelToMode('high')
    }
  },
  { immediate: true },
)

// Auto-show details if collection gets marked for deletion while viewing it
watch(
  () => workspaceStore.markedForDeletion,
  (marked) => {
    if (marked && currentView.value === 'workspace-detail') {
      if (!isMobileBottomPanelExpanded.value) {
        uiStore.expandMobileBottomPanel()
      }
      isWorkspaceInfoSheetOpen.value = false
    }
  },
)

// Reset scroll position when switching views
watch(
  () => currentView.value,
  () => {
    nextTick(() => {
      // Reset scroll position to top when switching views
      const container = scrollContainer.value
      if (container) {
        container.scrollTop = 0
      }
      schedulePhotosHeaderVisibility()
    })
  },
)

watch(
  [
    hasPhotos,
    hasTours,
    hasPOIs,
    isDetailLoading,
    () => photosHeaderRef.value,
    () => photosSectionRef.value,
    () => poiHeaderRef.value,
    isMobileBottomPanelExpanded,
  ],
  () => schedulePhotosHeaderVisibility(),
)

watch(panelHeight, () => scheduleMeasuredMobileBottomPanelHeightPublish(), {
  immediate: true,
  flush: 'post',
})

watch(
  [isDragging, isAnimating],
  ([dragging, animating]) => {
    if (dragging || animating) {
      startPanelHeightTracking()
      return
    }
    stopPanelHeightTracking()
    scheduleMeasuredMobileBottomPanelHeightPublish()
  },
  { immediate: true },
)

// Clean up event listeners
onUnmounted(() => {
  // Event listeners using useEventListener are automatically cleaned up

  // Clean up conditional document click listener
  if (removeDocumentClickListener) {
    removeDocumentClickListener()
    removeDocumentClickListener = null
  }

  // Clean up conditional dropdown click listener
  if (removeDropdownClickListener) {
    removeDropdownClickListener()
    removeDropdownClickListener = null
  }

  // Ensure mouse listeners are cleaned up if component unmounts mid-drag
  cleanupMouseListeners()

  if (loadingTextTimer) {
    clearTimeout(loadingTextTimer)
    loadingTextTimer = null
  }
  if (loadingIconTimer) {
    clearTimeout(loadingIconTimer)
    loadingIconTimer = null
  }

  stopPanelHeightTracking()
  cancelScheduledPanelHeightPublish()
  stopWorkspaceInfoDescriptionParentScrollLock()
  uiStore.setMobileBottomPanelHigh(false)
  uiStore.setMobileBottomPanelExpandedProgress(0)
  uiStore.setMobileBottomPanelHighProgress(0, toolbarHeaderHeight.value)
  uiStore.setMobileBottomPanelHeight(toolbarHeaderHeight.value)
})
</script>

<template>
  <div
    v-show="!isDialogOpen"
    ref="panelRef"
    data-testid="workspace-mobile-panel"
    :class="[
      // Component identifier
      'mobile-toolbar',
      // Position & layout (inset card; left/right/width come from the scoped CSS vars below)
      'fixed',
      'p-0',
      'flex',
      'flex-col',
      // Background & borders — self-contained floating white card.
      'bg-white',
      'rounded-2xl',
      'shadow-lg',
      'overflow-hidden',
      { 'select-none': isDragging, 'is-touch': isTouch },
    ]"
    :style="{
      height: `${panelHeight}px`,
      maxHeight: mobileSheetMaxHeight(floatingTabBarPanelOffset),
      paddingLeft: safeAreaInsets.left,
      paddingRight: safeAreaInsets.right,
      transition: isAnimating ? 'height 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
      touchAction: 'manipulation',
      willChange: 'height',
      top: 'auto',
      '--mobile-panel-bottom': floatingTabBarPanelOffset,
      '--mobile-panel-left': floatingTabBarPanelSideInset,
      '--mobile-panel-right': floatingTabBarPanelSideInset,
      zIndex: Z_INDEX.MOBILE_TOOLBAR,
    }"
    @touchstart.capture="handleScrollTouchStart"
    @touchmove.capture="handleScrollTouchMove"
    @touchend.capture="handleScrollTouchEnd"
    @touchcancel.capture="handleScrollTouchCancel"
    @contextmenu.capture="handleScrollContextMenu"
    @transitionend="handleMobileToolbarTransitionEnd"
  >
    <!-- Drag Handle - Handles both touch and mouse events -->
    <div
      data-testid="workspace-mobile-panel-handle"
      class="relative z-10 flex justify-center py-1.5 bg-white touch-none cursor-grab active:cursor-grabbing"
      :class="{ 'cursor-grabbing': isDragging }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @mousedown="handleMouseDown"
      @click="handleDragHandleClick"
    >
      <div
        class="w-12 h-1.5 bg-gray-300 rounded-full transition-colors"
        :class="{ 'bg-gray-400': isDragging }"
      ></div>
    </div>

    <!-- Collection stats: aggregate summary (the collection name lives in the floating top-centre
         title). A persistent header at peek + medium, mirroring the route sheet so both read the
         same. Distance · Time · Ascent · Avg matches the track's Distance · Time · Ascent · Descent. -->
    <button
      v-if="showCollectionStats"
      type="button"
      class="grid w-full auto-cols-fr grid-flow-col gap-x-3 px-4 pb-1 pt-0.5 text-left hover:cursor-pointer"
      aria-label="Expand collection"
      @click="handleHeaderClick"
    >
      <div class="flex min-w-0 flex-col leading-tight">
        <span class="truncate text-sm font-semibold tabular-nums text-neutral-900">
          {{ formatCollectionDistance(workspaceStore.totalDistance) }}
        </span>
        <span class="text-[11px] uppercase tracking-wide text-neutral-600">Distance</span>
      </div>
      <div
        v-if="formatCollectionDuration(workspaceStore.totalTimeElapsed)"
        class="flex min-w-0 flex-col leading-tight"
      >
        <span class="truncate text-sm font-semibold tabular-nums text-neutral-900">
          {{ formatCollectionDuration(workspaceStore.totalTimeElapsed) }}
        </span>
        <span class="text-[11px] uppercase tracking-wide text-neutral-600">Time</span>
      </div>
      <div
        v-if="visibleCollectionElevationGain !== null"
        class="flex min-w-0 flex-col leading-tight"
      >
        <span class="truncate text-sm font-semibold tabular-nums text-neutral-900">
          +{{ Math.round(visibleCollectionElevationGain) }}m
        </span>
        <span class="text-[11px] uppercase tracking-wide text-neutral-600">Ascent</span>
      </div>
      <div
        v-if="workspaceStore.totalAverageSpeed !== null"
        class="flex min-w-0 flex-col leading-tight"
      >
        <span class="truncate text-sm font-semibold tabular-nums text-neutral-900">
          {{ workspaceStore.totalAverageSpeed.toFixed(1) }} km/h
        </span>
        <span class="text-[11px] uppercase tracking-wide text-neutral-600">Avg</span>
      </div>
    </button>

    <!-- Header Section. Vertically centers its content so the collapsed peek shows the
         title centered between the drag handle and the tab bar's top edge. -->
    <div
      class="relative z-10 flex items-center px-3 border-gray-200 bg-white overflow-visible panel-header"
      :style="{
        minHeight: `${
          40 +
          (currentView === 'workspace-detail' && !isPinnedWorkspaceDetail
            ? 48 * highTransitionProgress
            : 8 * highTransitionProgress)
        }px`,
      }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div class="flex w-full items-center justify-between min-w-0">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <template v-if="highTransitionProgress > 0 && !isMobileBatchMode">
            <div
              data-testid="high-panel-title"
              class="pointer-events-none absolute flex min-w-0 flex-col items-center justify-center"
              :class="
                isCollectionHighTransition
                  ? 'inset-x-2 bottom-0 h-10'
                  : 'inset-x-28 top-1/2 -translate-y-1/2'
              "
            >
              <div
                class="flex max-w-full items-center justify-center gap-1"
                :style="isCollectionHighTransition ? collectionHighRevealStyle : undefined"
              >
                <h1
                  class="min-w-0 truncate font-semibold tracking-tight text-gray-900"
                  :class="isCollectionHighTransition ? 'text-xl' : 'text-lg'"
                  :title="highPanelHeaderTitle"
                >
                  {{ highPanelHeaderTitle }}
                </h1>
                <BaseIconButton
                  v-if="
                    currentView === 'workspace-detail' &&
                    !isPinnedWorkspaceDetail &&
                    isHighPanelHeader
                  "
                  data-testid="high-panel-collection-info"
                  size="sm"
                  class="pointer-events-auto shrink-0 text-gray-500"
                  title="Collection info"
                  aria-label="Open collection info"
                  @click.stop="openWorkspaceInfoSheet"
                >
                  <InfoIcon class="h-4 w-4" />
                </BaseIconButton>
              </div>
              <p
                v-if="
                  workspaceAuthorByline &&
                  currentView === 'workspace-detail' &&
                  !isPinnedWorkspaceDetail
                "
                class="truncate text-xs font-medium leading-tight text-gray-500"
                :style="collectionHighRevealStyle"
              >
                by {{ workspaceAuthorByline }}
              </p>
            </div>
          </template>

          <template v-else-if="currentView === 'workspaces' && !isMobileBatchMode">
            <div
              class="pointer-events-none absolute inset-x-14 top-1/2 flex min-w-0 -translate-y-1/2 items-center justify-center"
            >
              <button
                @click="handleHeaderClick"
                class="pointer-events-auto flex min-w-0 max-w-full items-center text-center hover:cursor-pointer"
              >
                <h1
                  class="truncate text-lg font-semibold tracking-tight text-gray-900"
                  :title="isMobileBottomPanelExpanded ? 'Zoom to collections' : 'Expand'"
                >
                  Collections
                </h1>
              </button>
            </div>
          </template>
          <!-- Collection detail: the title now lives in the floating top-center collection title
               (WorkspaceMapContainer); the collection actions stay on the right of this header. -->

          <!-- Pinned header — centered independently of side actions. -->
          <div
            v-else-if="isPinnedWorkspaceDetail && !isMobileBatchMode"
            class="pointer-events-none absolute inset-x-24 top-1/2 flex min-w-0 -translate-y-1/2 items-center justify-center"
          >
            <button
              @click="handleHeaderClick"
              class="pointer-events-auto flex min-w-0 max-w-full items-center text-center hover:cursor-pointer"
            >
              <h1 class="truncate text-lg font-semibold tracking-tight text-gray-900">Pinned</h1>
            </button>
          </div>

          <div
            v-else-if="currentView === 'all-activities' && !isMobileBatchMode"
            class="pointer-events-none absolute inset-x-14 top-1/2 flex min-w-0 -translate-y-1/2 items-center justify-center"
          >
            <button
              @click="handleHeaderClick"
              class="pointer-events-auto flex min-w-0 max-w-full items-center text-center hover:cursor-pointer"
            >
              <h1 class="truncate text-lg font-semibold tracking-tight text-gray-900">Library</h1>
            </button>
          </div>

          <!-- Tour detail header -->
          <div
            v-else-if="currentView === 'all-activities-tour-detail' && !isMobileBatchMode"
            class="flex items-center text-sm min-w-0 flex-1 overflow-x-hidden"
          >
            <!-- Back is handled by the floating top-left button. -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start gap-1">
                <div class="min-w-0 flex-1">
                  <span
                    class="block text-xl font-semibold text-gray-900 leading-snug wrap-break-word"
                    >{{ mobileTourDetailEffectiveName }}</span
                  >
                  <p
                    v-if="mobileTourDetailNameSubtitle"
                    class="truncate text-xs text-gray-500"
                    data-testid="mobile-tour-name-subtitle"
                  >
                    {{ mobileTourDetailNameSubtitle }}
                  </p>
                </div>
                <button
                  @click="openMobileRename"
                  class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer shrink-0 mt-0.5"
                  title="Rename"
                  aria-label="Rename tour"
                >
                  <PencilIcon class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          <!-- end left-side flex-1 -->

          <template v-if="isMobileBatchMode">
            <div class="absolute left-3 top-1/2 z-10 -translate-y-1/2">
              <button
                type="button"
                :disabled="currentBatchSelectableCount === 0"
                :class="[
                  mobileHeaderPillButtonClass,
                  currentBatchSelectableCount === 0
                    ? 'cursor-not-allowed text-gray-300'
                    : 'text-gray-700 hover:cursor-pointer hover:bg-gray-100',
                ]"
                @click="toggleSelectAllMobileBatchItems"
              >
                <span>{{ areAllCurrentBatchItemsSelected ? 'Deselect' : 'Select all' }}</span>
              </button>
            </div>
            <div
              class="pointer-events-none absolute inset-x-24 top-1/2 flex -translate-y-1/2 items-center justify-center"
            >
              <span class="truncate text-base font-semibold text-gray-900">
                {{ mobileBatchSelectionStore.selectedCount }} selected
              </span>
            </div>
            <div class="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center gap-[6px]">
              <button
                type="button"
                :class="mobileHeaderIconButtonClass"
                title="Close selection"
                aria-label="Close selection"
                @click="exitMobileBatchSelection"
              >
                <XIcon class="h-5 w-5" />
              </button>
            </div>
          </template>

          <!-- Right side actions: normal views -->
          <div
            v-if="!isMobileBatchMode"
            class="absolute right-3 z-10 flex -translate-y-1/2 items-center"
            :class="collectionActionRowTop === undefined ? 'top-1/2' : ''"
            :style="{ top: collectionActionRowTop }"
          >
            <!-- Collection detail keeps Share on the right; Select lives in the overflow menu. -->
            <template v-if="currentView === 'workspace-detail' && !isPinnedWorkspaceDetail">
              <button
                v-if="canShare"
                @click="openShareModal"
                :class="mobileHeaderIconButtonClass"
                title="Share collection"
                aria-label="Share collection"
              >
                <ShareIcon class="w-5 h-5" />
              </button>
            </template>
            <div
              v-if="props.showHighHeaderPrimaryAction"
              data-testid="high-header-primary-slot"
              class="overflow-hidden"
              :style="{
                width: `${(40 + (canShare ? 6 : 0)) * highHeaderButtonProgress}px`,
                opacity: highHeaderButtonProgress,
              }"
            >
              <div
                class="w-10"
                :style="{
                  marginLeft: canShare ? '6px' : '0',
                  transform: `translate3d(${40 * (1 - highHeaderButtonProgress)}px, 0, 0)`,
                }"
              >
                <slot name="high-header-create" />
              </div>
            </div>
          </div>
          <!-- Persistent map-fit action; collection contexts place it after their overflow menu. -->
          <div
            v-if="showFitMapButton && !isMobileBatchMode"
            class="absolute left-3 z-10 flex -translate-y-1/2 items-center"
            :class="collectionActionRowTop === undefined ? 'top-1/2' : ''"
            :style="{ top: collectionActionRowTop }"
          >
            <div
              v-if="props.showHighHeaderBack"
              data-testid="high-header-back-slot"
              class="overflow-hidden"
              :style="{
                width: `${42 * headerBackButtonProgress}px`,
                opacity: headerBackButtonProgress,
              }"
            >
              <button
                type="button"
                :class="mobileHeaderIconButtonClass"
                :style="{
                  transform: `translate3d(${-36 * (1 - headerBackButtonProgress)}px, 0, 0)`,
                }"
                :title="props.highHeaderBackAriaLabel"
                :aria-label="props.highHeaderBackAriaLabel"
                @click="emit('high-header-back')"
              >
                <ChevronLeftIcon class="h-5 w-5" />
              </button>
            </div>

            <button
              v-if="
                currentView === 'workspace-detail' ||
                currentView === 'all-activities' ||
                currentView === 'workspaces'
              "
              ref="dropdownButtonRef"
              @click="showDropdownMenu = !showDropdownMenu"
              :class="mobileHeaderIconButtonClass"
              :title="
                isPinnedWorkspaceDetail
                  ? 'Pinned options'
                  : currentView === 'all-activities'
                    ? 'Library options'
                    : currentView === 'workspaces'
                      ? 'Collections options'
                      : 'Collection options'
              "
              :aria-label="
                isPinnedWorkspaceDetail
                  ? 'Pinned options'
                  : currentView === 'all-activities'
                    ? 'Library options'
                    : currentView === 'workspaces'
                      ? 'Collections options'
                      : 'Collection options'
              "
            >
              <MoreHorizontalIcon class="w-5 h-5" />
            </button>

            <button
              type="button"
              :class="[mobileHeaderIconButtonClass, 'ml-[6px]']"
              :disabled="!canFitCurrentMapContext"
              title="Center map"
              aria-label="Center map"
              @click="handleFitMapToCurrentContext"
            >
              <FocusIcon class="h-5 w-5" />
            </button>

            <!-- Dropdown Menu - Use teleport to avoid overflow clipping -->
            <Teleport to="body">
              <div
                v-if="showDropdownMenu"
                ref="dropdownMenuRef"
                class="fixed w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1 max-w-[calc(100vw-2rem)]"
                :style="{
                  zIndex: Z_INDEX.MOBILE_TOOLBAR_DROPDOWN,
                  top: dropdownPosition.top + 'px',
                  left: dropdownPosition.left + 'px',
                }"
                @click="showDropdownMenu = false"
              >
                <template v-if="isPinnedWorkspaceDetail">
                  <button
                    :disabled="currentBatchSelectableCount === 0"
                    @click="
                      () => {
                        if (currentBatchSelectableCount === 0) return
                        showDropdownMenu = false
                        enterMobileBatchSelection()
                      }
                    "
                    class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                    :title="
                      currentBatchSelectableCount === 0 ? 'No items to select' : 'Select items'
                    "
                  >
                    <CircleCheckIcon class="h-4 w-4" />
                    Select
                  </button>

                  <button
                    data-testid="mobile-pinned-unpin-all"
                    :disabled="!canUnpinAllPinnedItems"
                    @click="handleUnpinAll"
                    class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                    :title="hasItemsToUnpin ? 'Unpin all linked items' : 'No linked items to unpin'"
                  >
                    <PinOffIcon class="h-4 w-4" />
                    Unpin all
                  </button>
                </template>

                <template
                  v-else-if="currentView === 'all-activities' || currentView === 'workspaces'"
                >
                  <button
                    :disabled="currentBatchSelectableCount === 0"
                    @click="
                      () => {
                        if (currentBatchSelectableCount === 0) return
                        showDropdownMenu = false
                        enterMobileBatchSelection()
                      }
                    "
                    class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                    :title="
                      currentBatchSelectableCount === 0 ? 'No items to select' : 'Select items'
                    "
                  >
                    <CircleCheckIcon class="h-4 w-4" />
                    Select
                  </button>
                </template>

                <template v-else>
                  <button
                    :disabled="currentBatchSelectableCount === 0"
                    @click="
                      () => {
                        if (currentBatchSelectableCount === 0) return
                        showDropdownMenu = false
                        enterMobileBatchSelection()
                      }
                    "
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                    :title="
                      currentBatchSelectableCount === 0 ? 'No items to select' : 'Select items'
                    "
                  >
                    <CircleCheckIcon class="w-4 h-4" />
                    Select
                  </button>

                  <button
                    :disabled="!hasCurrentWorkspacePinnableItems"
                    @click="handleCurrentWorkspacePinToggle"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                    :title="
                      hasCurrentWorkspacePinnableItems
                        ? areAllCurrentWorkspaceItemsPinned
                          ? 'Unpin all items'
                          : 'Pin all items'
                        : 'No items to pin'
                    "
                  >
                    <component
                      :is="areAllCurrentWorkspaceItemsPinned ? PinOffIcon : PinIcon"
                      class="w-4 h-4"
                    />
                    {{ areAllCurrentWorkspaceItemsPinned ? 'Unpin all' : 'Pin all' }}
                  </button>

                  <hr class="my-1 border-gray-200" />

                  <button
                    v-if="isTauriApp"
                    @click="handleCurrentWorkspaceOffline"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer flex items-center gap-2"
                  >
                    <DownloadIcon class="w-4 h-4" />
                    {{
                      isCurrentWorkspaceOffline ? 'Manage offline data' : 'Make available offline'
                    }}
                  </button>

                  <button
                    @click="
                      () => {
                        startEditingName()
                        showDropdownMenu = false
                      }
                    "
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer flex items-center gap-2"
                  >
                    <PencilIcon class="w-4 h-4" />
                    Rename
                  </button>

                  <button
                    @click="openWorkspaceInfoSheet"
                    class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:cursor-pointer flex items-center gap-2"
                  >
                    <InfoIcon class="w-4 h-4" />
                    Collection info
                  </button>

                  <hr v-if="canDelete || canRestore || canLeave" class="my-1 border-gray-200" />

                  <button
                    v-if="canLeave"
                    @click="handleLeaveClick"
                    class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:cursor-pointer flex items-center gap-2"
                  >
                    <LogOutIcon class="w-4 h-4" />
                    Leave Collection
                  </button>

                  <button
                    v-if="canDelete"
                    @click="handleDeleteClick"
                    class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:cursor-pointer flex items-center gap-2"
                  >
                    <TrashIcon class="w-4 h-4" />
                    Delete Collection
                  </button>

                  <button
                    v-if="canRestore"
                    @click="handleRestoreClick"
                    class="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 hover:cursor-pointer flex items-center gap-2"
                  >
                    <RestoreIcon class="w-4 h-4" />
                    Restore Collection
                  </button>
                </template>
              </div>
            </Teleport>
          </div>
        </div>
      </div>

      <!-- Selection mode overlay — shown over header when panel is collapsed -->
      <div
        v-if="
          showLegacyLibrarySelectionOverlay &&
          !isMobileBottomPanelExpanded &&
          currentView === 'all-activities' &&
          libraryStore.isSelectionMode
        "
        class="absolute inset-0 bg-white flex items-center px-2 gap-1 z-10"
      >
        <button
          @click="libraryStore.exitSelectionMode()"
          class="flex items-center justify-center w-7 h-7 rounded text-gray-500 hover:bg-gray-100 transition-colors shrink-0"
          aria-label="Exit selection mode"
        >
          <XIcon class="w-4 h-4" />
        </button>
        <span class="text-[13px] font-medium text-gray-900 mr-auto">
          {{ libraryStore.selectedTourIds.size }} selected
        </span>
        <button
          v-if="libraryStore.selectedTourIds.size <= 1"
          @click="activityRouteCollectionRef?.openEditForSelectedTour()"
          :disabled="libraryStore.selectedTourIds.size === 0"
          class="flex items-center justify-center w-8 h-8 rounded transition-colors"
          :class="
            libraryStore.selectedTourIds.size === 0
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer'
          "
          title="Edit"
        >
          <SettingsIcon class="w-4 h-4" />
        </button>
        <button
          @click="activityRouteCollectionRef?.openBulkRename()"
          :disabled="libraryStore.selectedTourIds.size === 0"
          class="flex items-center justify-center w-8 h-8 rounded transition-colors"
          :class="
            libraryStore.selectedTourIds.size === 0
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer'
          "
          title="Rename"
        >
          <PencilIcon class="w-4 h-4" />
        </button>
        <button
          @click="activityRouteCollectionRef?.openBulkAddToCollection()"
          :disabled="libraryStore.selectedTourIds.size === 0"
          class="flex items-center justify-center w-8 h-8 rounded transition-colors"
          :class="
            libraryStore.selectedTourIds.size === 0
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer'
          "
          title="Add to collection"
        >
          <SquarePlusIcon class="w-4 h-4" />
        </button>
        <button
          @click="activityRouteCollectionRef?.openBulkDelete()"
          :disabled="libraryStore.selectedTourIds.size === 0"
          class="flex items-center justify-center w-8 h-8 rounded transition-colors"
          :class="
            libraryStore.selectedTourIds.size === 0
              ? 'text-red-200 cursor-not-allowed'
              : 'text-red-500 hover:text-red-600 hover:bg-red-50 hover:cursor-pointer'
          "
          title="Delete"
        >
          <TrashIcon class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Tour detail date row (non-scrolling, below header) -->
    <div
      v-if="currentView === 'all-activities-tour-detail' && mobileTourDetailTour?.startDate"
      class="px-4 pb-2 bg-white text-[12px] text-gray-400 shrink-0"
    >
      {{ formatMobileDate(mobileTourDetailTour.startDate)
      }}<template v-if="mobileTourDetailTour.endDate">
        — {{ formatMobileDate(mobileTourDetailTour.endDate) }}</template
      >
    </div>

    <!-- Collection Detail Action Bar — outside scroll container so it never scrolls/rubberbands; order-last keeps it visually at bottom -->
    <div
      v-if="
        showLegacyCollectionActionBar &&
        currentView === 'workspace-detail' &&
        panelHeight > toolbarHeaderHeight + 50
      "
      data-tour-actions-row
      class="flex items-center h-12 px-2 bg-white border-t border-gray-200 shrink-0 order-last"
    >
      <!-- Expanded search state -->
      <template v-if="tourSearchExpanded">
        <div class="flex-1 relative min-w-0">
          <SearchIcon
            class="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
          />
          <input
            ref="tourSearchInputRef"
            v-model="tourSearchQuery"
            type="text"
            placeholder="Search tours..."
            class="w-full pl-7 pr-2.5 py-1 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            @keydown.esc.prevent="closeTourSearch"
          />
        </div>
        <button
          @click="closeTourSearch"
          class="flex items-center justify-center w-9 h-9 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded-md transition-colors shrink-0"
          title="Close search"
          aria-label="Close search"
        >
          <XIcon class="w-5 h-5" />
        </button>
      </template>

      <!-- Collapsed state -->
      <template v-else>
        <div class="flex items-center justify-center gap-1 w-full">
          <!-- Add route -->
          <button
            @click.stop="handleAddEmptyTour"
            :disabled="isContentLoading"
            class="flex items-center justify-center w-9 h-9 text-primary-600 hover:text-primary-700 hover:bg-primary-50 hover:cursor-pointer rounded-md transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Add route"
            aria-label="Add route"
          >
            <PlusIcon class="w-5 h-5" />
          </button>

          <!-- Upload (combined GPX/FIT + Photos) -->
          <div class="relative shrink-0" data-tour-upload-menu>
            <button
              @click="toggleTourUploadMenu"
              :disabled="isContentLoading"
              class="flex items-center justify-center w-9 h-9 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 hover:cursor-pointer rounded-md transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              :class="tourUploadMenuOpen ? 'bg-emerald-50' : ''"
              title="Upload GPX / FIT or photos"
              aria-label="Upload"
            >
              <UploadIcon class="w-5 h-5" />
            </button>
            <Teleport to="body">
              <div
                v-if="tourUploadMenuOpen"
                :style="tourUploadMenuStyle"
                data-tour-upload-menu
                class="fixed z-9999 w-48 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
              >
                <button
                  @click="handleGpxUploadClick"
                  class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                >
                  <UploadIcon class="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Import GPX / FIT</span>
                </button>
                <button
                  @click="handlePhotoUploadClick"
                  class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 text-gray-700 hover:bg-gray-50 hover:cursor-pointer transition-colors"
                >
                  <ImagePlusIcon class="w-3.5 h-3.5 text-primary-600 shrink-0" />
                  <span>Add photos</span>
                </button>
              </div>
            </Teleport>
          </div>

          <!-- Loading spinner -->
          <div
            v-if="isContentLoading"
            class="flex items-center justify-center w-9 h-9 shrink-0"
            title="Processing content"
            aria-live="polite"
          >
            <LoaderIcon class="w-5 h-5 animate-spin text-primary-600" />
          </div>

          <!-- Search -->
          <button
            @click.stop="openTourSearch"
            class="flex items-center justify-center w-9 h-9 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:cursor-pointer rounded-md transition-colors shrink-0"
            title="Search tours"
            aria-label="Search tours"
          >
            <SearchIcon class="w-5 h-5" />
          </button>

          <!-- Sort -->
          <div class="relative shrink-0" data-tour-sort-menu>
            <button
              @click="toggleTourSortMenu"
              class="flex items-center justify-center w-9 h-9 rounded-md transition-colors hover:cursor-pointer"
              :class="
                tourSortMenuOpen
                  ? 'text-emerald-600 bg-emerald-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              "
              title="Sort tours"
              aria-label="Sort tours"
            >
              <ArrowUpDownIcon class="w-5 h-5 shrink-0" />
            </button>
            <Teleport to="body">
              <div
                v-if="tourSortMenuOpen"
                :style="tourSortMenuStyle"
                data-tour-sort-menu
                class="fixed z-9999 w-56 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden py-1"
              >
                <button
                  v-for="opt in tourSortOptions"
                  :key="`${opt.sortBy}-${opt.sortDir}`"
                  @click="setTourSort(opt)"
                  class="w-full px-3 py-2 text-xs text-left flex items-center gap-2 hover:cursor-pointer transition-colors"
                  :class="
                    isActiveTourSort(opt)
                      ? 'text-emerald-700 bg-emerald-50 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  "
                >
                  <CheckIcon
                    v-if="isActiveTourSort(opt)"
                    class="w-3.5 h-3.5 text-emerald-600 shrink-0"
                  />
                  <span v-else class="w-3.5 h-3.5 shrink-0" />
                  {{ opt.label }}
                </button>
              </div>
            </Teleport>
          </div>
        </div>
      </template>
    </div>

    <!-- Collections Overview — always mounted (outside panelHeight guard) so onMounted only
         fires once and activateAllWorkspaces() is not re-triggered on every panel expand. -->
    <div
      v-show="currentView === 'workspaces' && panelHeight > toolbarHeaderHeight + 50"
      class="relative z-10 flex-1 min-h-0"
    >
      <WorkspaceSearchPanel
        fill-container
        :selection-mode="isMobileBatchMode && mobileBatchSurface === 'collections-overview'"
        :selected-collection-ids="mobileBatchSelectionStore.collections"
        @select-workspace="handleWorkspaceSelect"
        @toggle-selection="(id) => mobileBatchSelectionStore.toggle('collections', id)"
        @make-offline="(id) => emit('make-offline', id)"
      />
    </div>

    <template v-if="panelHeight > toolbarHeaderHeight + 50">
      <!-- Collection Detail View — scroll container starts here -->
      <Transition name="slide-fade-left" mode="out-in">
        <div
          v-if="currentView === 'workspace-detail'"
          key="collection-detail"
          class="flex flex-1 min-h-0 flex-col overflow-hidden"
        >
          <div
            ref="scrollContainer"
            data-mobile-panel-scroll
            class="flex-1 overflow-y-auto overflow-x-hidden touch-pan-y scrollable-content min-h-0 relative"
            @scroll="recomputePhotosHeaderVisibility"
          >
            <!-- Loading State (delayed bike) -->
            <div v-if="isDetailLoading" class="p-4 text-center text-gray-500">
              <div class="relative w-full h-8 mb-2 overflow-hidden">
                <BikeIcon
                  v-if="showLoadingIcon"
                  class="w-8 h-8 text-primary-500 animate-bike-ride"
                />
              </div>
              <p v-if="showLoadingIcon" class="text-sm">Collection loading...</p>
            </div>

            <template v-else>
              <button
                v-if="
                  isCollectionHighTransition &&
                  workspaceStore.coverPhotoUrl &&
                  workspaceStore.coverPhotoClientId
                "
                type="button"
                class="mx-4 mt-2 mb-3 block w-[calc(100%-2rem)] overflow-hidden rounded-2xl bg-gray-100 text-left hover:cursor-pointer"
                :style="{
                  ...collectionHighRevealStyle,
                  height: `${176 * collectionHighRevealProgress}px`,
                  marginTop: `${8 * collectionHighRevealProgress}px`,
                  marginBottom: `${12 * collectionHighRevealProgress}px`,
                }"
                aria-label="Open cover photo"
                @click="handleViewCoverPhoto"
              >
                <img
                  :src="workspaceStore.coverPhotoUrl"
                  alt=""
                  class="h-44 w-full select-none object-cover"
                  draggable="false"
                  @contextmenu.prevent
                />
              </button>

              <div
                v-if="
                  currentView === 'workspace-detail' &&
                  !isPinnedWorkspaceDetail &&
                  workspaceStore.markedForDeletion
                "
                class="bg-white"
              >
                <div
                  v-if="workspaceStore.markedForDeletion"
                  class="border-b border-gray-100 border-l-4 border-red-400 bg-red-50 p-3"
                >
                  <p class="text-sm text-red-800">
                    <strong>Collection marked for deletion</strong>
                  </p>
                  <p class="mt-1 text-xs text-red-600">
                    Will be permanently deleted in {{ daysUntilDeletion }}
                    {{ daysUntilDeletion === 1 ? 'day' : 'days' }}. Click the undo button to
                    restore.
                  </p>
                </div>
              </div>

              <div
                v-if="
                  isPinnedWorkspaceDetail && isPinnedWorkspaceEmpty && isPinnedWorkspaceDisplayReady
                "
                class="p-3"
              >
                <PinnedEmptyState
                  compact
                  @go-library="navigatePinnedEmptyStateToLibrary"
                  @go-collections="navigatePinnedEmptyStateToCollections"
                />
              </div>

              <!-- Activities & Routes Header - matches the desktop collection detail treatment -->
              <div>
                <div
                  :class="[
                    'px-3',
                    'bg-white',
                    'sticky',
                    'top-0',
                    'z-10',
                    'hover:cursor-pointer',
                    'hover:bg-gray-200',
                    'transition-colors',
                  ]"
                  @click="scrollToTours"
                >
                  <div class="flex h-[38px] items-center justify-between gap-2">
                    <div class="flex min-w-0 items-center gap-1.5">
                      <h3 class="shrink-0 text-[16px] font-semibold text-gray-900">
                        Activities &amp; Routes ({{ workspaceStore.tourFileCount }})
                      </h3>
                    </div>
                    <!-- Create lives in the floating "+" map button, not per section. -->
                    <div class="flex items-center gap-1">
                      <button
                        class="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:cursor-pointer hover:bg-gray-200 hover:text-gray-800 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                        :title="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                        :aria-label="isAnyTourVisible ? 'Hide all tours' : 'Show all tours'"
                        :disabled="!hasTours"
                        @click.stop="handleToggleAllToursVisibility"
                      >
                        <component :is="isAnyTourVisible ? EyeIcon : EyeOffIcon" class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Tours List -->
                <div v-if="hasTours" class="border-b border-gray-100">
                  <TourList
                    :override-tours="detailDisplayTours"
                    :selection-mode="
                      isMobileBatchMode && mobileBatchSurface !== 'collections-overview'
                    "
                    :selected-tour-ids="mobileBatchSelectionStore.tours"
                    @zoom-tour="handleZoomTour"
                    @select-tour="handleSelectTour"
                    @enter-track-edit="handleEnterTrackEdit"
                    @toggle-selection="(id) => mobileBatchSelectionStore.toggle('tours', id)"
                  />
                </div>
                <div
                  v-if="!hasTours && (!isPinnedWorkspaceDetail || isPinnedWorkspaceEmpty)"
                  :class="[
                    'border-b p-3',
                    isPinnedWorkspaceDetail && isPinnedWorkspaceDisplayReady
                      ? 'border-emerald-100 bg-emerald-50/70'
                      : 'border-gray-100',
                  ]"
                >
                  <p
                    v-if="isPinnedWorkspaceDetail && isPinnedWorkspaceDisplayReady"
                    class="text-sm text-gray-500"
                  >
                    No pinned activities or routes yet.
                  </p>
                  <div
                    v-if="
                      isPinnedWorkspaceDetail &&
                      isPinnedWorkspaceEmpty &&
                      isPinnedWorkspaceDisplayReady &&
                      canAddCollectionContent
                    "
                    class="mt-3 flex flex-wrap gap-2"
                  >
                    <button
                      type="button"
                      aria-label="Draw route"
                      :disabled="isContentLoading"
                      class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70 disabled:cursor-not-allowed disabled:opacity-50"
                      @click.stop="handleDrawRouteIntoCollection"
                    >
                      <RouteIcon class="h-3.5 w-3.5 text-emerald-700" />
                      <span>Draw route</span>
                    </button>
                    <button
                      type="button"
                      aria-label="Import"
                      :disabled="isContentLoading"
                      class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70 disabled:cursor-not-allowed disabled:opacity-50"
                      @click.stop="triggerMixedUpload"
                    >
                      <UploadIcon class="h-3.5 w-3.5 text-emerald-700" />
                      <span>Import</span>
                    </button>
                  </div>
                  <CollectionRouteEmptyState
                    v-else-if="canAddCollectionContent && isPinnedWorkspaceContentReady"
                    :busy="isContentLoading"
                    stacked
                    @import-gpx="triggerGpxUpload"
                    @import-photo="triggerPhotoUpload"
                    @draw-route="handleDrawRouteIntoCollection"
                  />
                  <p v-else-if="isPinnedWorkspaceContentReady" class="text-sm text-gray-500">
                    No activities or routes in this collection.
                  </p>
                </div>
              </div>

              <div>
                <!-- Points of Interest Header — sticky + icon order consistent with the
                     Activities & Routes / Photos headers (Add on the left, visibility eye last). -->
                <div
                  ref="poiHeaderRef"
                  :class="[
                    'px-3',
                    'bg-white',
                    'sticky',
                    'top-0',
                    'z-20',
                    'transition-colors',
                    'hover:cursor-pointer',
                    'hover:bg-gray-200',
                  ]"
                >
                  <div class="flex h-[38px] items-center justify-between gap-2">
                    <h3 class="text-[16px] font-semibold text-gray-900">
                      Points of Interest ({{ workspaceStore.allWorkspacePOIs.length }})
                    </h3>
                    <div class="flex items-center gap-1">
                      <button
                        type="button"
                        class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:cursor-pointer hover:bg-gray-200 hover:text-gray-800"
                        :title="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                        :aria-label="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                        @click.stop="handleTogglePOIsVisibility"
                      >
                        <EyeIcon v-if="uiStore.arePOIsVisible" class="h-4 w-4" />
                        <EyeOffIcon v-else class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="hasPOIs" class="bg-white">
                  <WorkspacePOIList
                    :selected-poi-id="props.selectedPoiId ?? null"
                    :selection-mode="
                      isMobileBatchMode && mobileBatchSurface !== 'collections-overview'
                    "
                    :selected-poi-ids="mobileBatchSelectionStore.pois"
                    @zoom-poi="handleZoomPOI"
                    @open-poi-detail="handleOpenPOIDetail"
                    @toggle-selection="(id) => mobileBatchSelectionStore.toggle('pois', id)"
                  />
                </div>
                <div
                  v-else-if="!isPinnedWorkspaceDetail || isPinnedWorkspaceEmpty"
                  :class="[
                    'p-4',
                    isPinnedWorkspaceDetail && isPinnedWorkspaceDisplayReady
                      ? 'bg-emerald-50/70'
                      : 'bg-white',
                  ]"
                >
                  <p class="text-sm text-gray-500">No points of interest in this collection.</p>
                  <div
                    v-if="
                      isPinnedWorkspaceDetail &&
                      isPinnedWorkspaceEmpty &&
                      isPinnedWorkspaceDisplayReady
                    "
                    class="mt-3 flex flex-wrap gap-2"
                  >
                    <button
                      v-if="canAddPOIToCollection"
                      type="button"
                      aria-label="Mark POI"
                      class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70"
                      @click.stop="requestCollectionPOI"
                    >
                      <MapPinIcon class="h-3.5 w-3.5 text-emerald-700" />
                      <span>Mark POI</span>
                    </button>
                    <button
                      v-if="canAddCollectionContent"
                      type="button"
                      aria-label="Import POIs"
                      :disabled="isContentLoading"
                      class="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:cursor-pointer hover:bg-emerald-100/70 disabled:cursor-not-allowed disabled:opacity-50"
                      @click.stop="triggerMixedUpload"
                    >
                      <UploadIcon class="h-3.5 w-3.5 text-emerald-700" />
                      <span>Import</span>
                    </button>
                  </div>
                </div>
              </div>

              <div ref="photosSectionRef">
                <!-- Photos Header - always renders (like the POI header), even with 0 photos.
                     Becomes sticky when reached. -->
                <div
                  :class="[
                    'px-3',
                    'bg-white',
                    'sticky',
                    'top-0',
                    'z-20',
                    'hover:cursor-pointer',
                    'hover:bg-gray-200',
                    'transition-colors',
                  ]"
                  ref="photosHeaderRef"
                  @click="scrollToPhotos"
                >
                  <div class="flex h-[38px] items-center justify-between gap-2">
                    <div class="flex min-w-0 items-center gap-1.5">
                      <h3 class="text-[16px] font-semibold text-gray-900">
                        Photos ({{ workspaceStore.photoFileCount }})
                      </h3>
                      <span
                        v-if="isPhotoFiltering"
                        class="ml-0.5 shrink-0 text-[11px] text-gray-400"
                      >
                        {{ `${filteredPhotoCount}/${totalPhotoCount} listed` }}
                      </span>
                    </div>
                    <button
                      class="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:cursor-pointer hover:bg-gray-200 hover:text-gray-700"
                      :title="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                      :aria-label="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                      @click.stop="handleTogglePhotosVisibility"
                    >
                      <component :is="arePhotosVisible ? EyeIcon : EyeOffIcon" class="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <!-- Photos List -->
                <div class="border-b border-gray-100">
                  <PhotoList
                    v-if="hasPhotos"
                    :dimmed="!arePhotosVisible"
                    :override-photos="filteredPhotoList"
                    :can-delete-photos="canDeletePhotos"
                    :can-set-cover-photo="canSetCollectionCover"
                    :animate-pinned-removal="isPinnedWorkspaceDetail"
                    :selection-mode="
                      isMobileBatchMode && mobileBatchSurface !== 'collections-overview'
                    "
                    :selected-photo-ids="mobileBatchSelectionStore.photos"
                    @view-photo="handleViewPhoto"
                    @zoom-photo="handlePhotoZoom"
                    @toggle-selection="(id) => mobileBatchSelectionStore.toggle('photos', id)"
                  />
                  <div
                    v-else-if="!isPinnedWorkspaceDetail || isPinnedWorkspaceEmpty"
                    class="bg-white p-4"
                  >
                    <p class="text-sm text-gray-500">No photos in this collection.</p>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <!-- Pinned POI quick-access footer — mirrors the pinned Photos footer so the Points of
               Interest section behaves the same on scroll (appears when its header is out of view). -->
          <div
            v-if="showPinnedPOIHeader"
            data-testid="pinned-poi-header"
            class="shrink-0 border-t border-gray-100 bg-white px-3 transition-colors hover:cursor-pointer hover:bg-gray-200"
            @click="scrollToPOIs"
          >
            <div class="flex h-[38px] items-center justify-between gap-2">
              <h3 class="text-[16px] font-semibold text-gray-900">
                Points of Interest ({{ workspaceStore.allWorkspacePOIs.length }})
              </h3>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:cursor-pointer hover:bg-gray-200 hover:text-gray-800"
                  :title="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                  :aria-label="uiStore.arePOIsVisible ? 'Hide POIs' : 'Show POIs'"
                  @click.stop="handleTogglePOIsVisibility"
                >
                  <EyeIcon v-if="uiStore.arePOIsVisible" class="h-4 w-4" />
                  <EyeOffIcon v-else class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="showPinnedPhotosHeader"
            data-testid="pinned-photos-header"
            class="shrink-0 border-t border-gray-100 border-b border-gray-100 bg-white px-3 transition-colors hover:cursor-pointer hover:bg-gray-200"
            @click="scrollToPhotos"
          >
            <div class="flex h-[38px] items-center justify-between gap-2">
              <div class="flex min-w-0 items-center gap-1.5">
                <h3 class="text-[16px] font-semibold text-gray-900">
                  Photos ({{ workspaceStore.photoFileCount }})
                </h3>
                <span v-if="isPhotoFiltering" class="ml-0.5 shrink-0 text-[11px] text-gray-400">
                  {{ `${filteredPhotoCount}/${totalPhotoCount} listed` }}
                </span>
              </div>
              <button
                class="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:cursor-pointer hover:bg-gray-200 hover:text-gray-700"
                :title="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                :aria-label="arePhotosVisible ? 'Hide all photos' : 'Show all photos'"
                @click.stop="handleTogglePhotosVisibility"
              >
                <component :is="arePhotosVisible ? EyeIcon : EyeOffIcon" class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </template>

    <!-- My Activities & Routes system collection —
         Outside the panelHeight guard so it stays mounted on collapse,
         keeping isAllActivitiesView true and tracks rendered. -->
    <div
      v-show="currentView === 'all-activities' || currentView === 'all-activities-tour-detail'"
      class="flex-1 min-h-0 overflow-hidden"
      :class="{ hidden: currentView === 'all-activities-tour-detail' }"
    >
      <ActivityRouteCollection
        ref="activityRouteCollectionRef"
        @back="handleBackToWorkspaces"
        @navigate-to-workspace="handleWorkspaceSelect"
        @open-mobile-tour-detail="handleOpenMobileTourDetail"
        @tour-detail-loaded="handleTourDetailLoaded"
        @zoom-tour="handleZoomTour"
      />
    </div>

    <!-- Tour detail panel (mobile, in-panel hierarchy) -->
    <div
      v-if="currentView === 'all-activities-tour-detail' && mobileTourDetailTour"
      class="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4"
    >
      <!-- Quick stats row — distance · elevation · duration -->
      <div
        v-if="
          mobileTourDetailTour.distance ||
          mobileTourDetailElevationGain ||
          formatMobileDuration(mobileTourDetailTour.startDate, mobileTourDetailTour.endDate)
        "
        class="text-[13px] text-gray-500 flex flex-wrap gap-x-1.5 gap-y-0.5 items-center"
      >
        <span v-if="mobileTourDetailTour.distance">{{
          formatMobileDistance(mobileTourDetailTour.distance)
        }}</span>
        <span
          v-if="mobileTourDetailTour.distance && mobileTourDetailElevationGain"
          class="text-gray-300"
          >·</span
        >
        <span v-if="mobileTourDetailElevationGain"
          >↑{{ Math.round(mobileTourDetailElevationGain) }} m</span
        >
        <span
          v-if="
            (mobileTourDetailTour.distance || mobileTourDetailElevationGain) &&
            formatMobileDuration(mobileTourDetailTour.startDate, mobileTourDetailTour.endDate)
          "
          class="text-gray-300"
          >·</span
        >
        <span
          v-if="formatMobileDuration(mobileTourDetailTour.startDate, mobileTourDetailTour.endDate)"
          >{{
            formatMobileDuration(mobileTourDetailTour.startDate, mobileTourDetailTour.endDate)
          }}</span
        >
      </div>

      <!-- Loading skeleton -->
      <template v-if="mobileDetailLoading">
        <div class="space-y-1.5 animate-pulse">
          <div class="h-2.5 w-20 bg-gray-200 rounded"></div>
          <div class="h-2 w-44 bg-gray-100 rounded"></div>
          <div class="h-2 w-36 bg-gray-100 rounded"></div>
        </div>
      </template>

      <template v-else>
        <!-- Extended stats grid — avg speed, start time, end time -->
        <div
          v-if="
            mobileDetailAvgSpeed !== null ||
            mobileDetailStartTime !== null ||
            mobileDetailEndTime !== null
          "
          class="grid grid-cols-3 gap-x-4 gap-y-2 text-[11px]"
        >
          <div v-if="mobileDetailAvgSpeed !== null">
            <p class="text-gray-400 uppercase tracking-wide font-semibold text-[9px]">Avg Speed</p>
            <p class="text-emerald-600 font-medium">
              {{ Number(mobileDetailAvgSpeed).toFixed(1) }} km/h
            </p>
          </div>
          <div v-if="mobileDetailStartTime !== null">
            <p class="text-gray-400 uppercase tracking-wide font-semibold text-[9px]">Start</p>
            <p class="text-gray-700">{{ formatMobileDateTime(mobileDetailStartTime) }}</p>
          </div>
          <div v-if="mobileDetailEndTime !== null">
            <p class="text-gray-400 uppercase tracking-wide font-semibold text-[9px]">End</p>
            <p class="text-gray-700">{{ formatMobileDateTime(mobileDetailEndTime) }}</p>
          </div>
        </div>

        <!-- Collections -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
            Collections
          </p>
          <div class="flex flex-wrap items-center gap-1.5">
            <BaseRemovableTag
              v-for="ws in mobileTourDetailTour.workspaces"
              :key="ws.clientId"
              :label="ws.name"
              size="md"
              :remove-label="`Remove from ${ws.name}`"
              @select="handleWorkspaceSelect(ws.clientId, ws.name)"
              @remove="mobileRemoveFromWorkspace(mobileTourDetailTour, ws.clientId)"
            />
            <button
              @click="openMobileAddToCol(mobileTourDetailTour)"
              class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors cursor-pointer shrink-0 text-[12px] font-medium"
            >
              <SquarePlusIcon class="w-3 h-3 shrink-0" />
              Add to collection
            </button>
          </div>
        </div>

        <!-- Description — inline editable -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            Description
          </p>
          <template v-if="mobileDescriptionEditOpen">
            <textarea
              ref="mobileDescriptionTextareaRef"
              v-model="mobileDescriptionEditValue"
              rows="3"
              class="w-full text-sm text-gray-700 bg-white border border-primary-300 rounded px-2 py-1.5 outline-none resize-none"
              placeholder="Add a description…"
              @keyup.escape.stop="cancelMobileDescriptionEdit"
              @blur="saveMobileDescriptionEdit"
            ></textarea>
          </template>
          <template v-else>
            <p
              v-if="mobileDetail?.description ?? mobileTourDetailTour.description"
              class="text-sm text-gray-600 whitespace-pre-line cursor-pointer hover:bg-white/60 rounded px-0.5 -mx-0.5"
              @click="
                startMobileDescriptionEdit(
                  mobileDetail?.description ?? mobileTourDetailTour.description ?? '',
                )
              "
            >
              {{ mobileDetail?.description ?? mobileTourDetailTour.description }}
            </p>
            <p
              v-else
              class="text-sm text-gray-400 italic cursor-pointer hover:text-gray-500"
              @click="startMobileDescriptionEdit('')"
            >
              Add description…
            </p>
          </template>
        </div>

        <!-- Tracks -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Tracks ({{ mobileDetail?.tracks.length ?? mobileTourDetailTour.tracks.length }})
          </p>
          <template v-if="mobileDetail?.tracks.length">
            <div
              v-for="(track, ti) in mobileDetail.tracks"
              :key="track.clientId ?? ti"
              class="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0"
            >
              <span
                class="w-2 h-2 rounded-full shrink-0 mt-1.5"
                :style="{
                  backgroundColor: TrackColorService.getTourColor(
                    mobileTourDetailTour.clientId,
                    track.color,
                  ),
                }"
              ></span>
              <div class="flex-1 min-w-0">
                <p class="text-[13px] text-gray-700 leading-snug">
                  {{ track.name || `Track ${ti + 1}` }}
                </p>
                <p class="text-[11px] text-gray-400 mt-0.5">
                  {{ formatMobileDistance(calculateTrackDistance(track))
                  }}<template v-if="getVisibleMobileTrackElevationGain(track)">
                    · ↑{{ Math.round(getVisibleMobileTrackElevationGain(track) ?? 0) }}m</template
                  ><template v-if="getTrackElevationLoss(track)">
                    · ↓{{ Math.round(getTrackElevationLoss(track) ?? 0) }}m</template
                  >
                </p>
              </div>
              <button
                @click="openMobileTrackActionSheet(mobileTourDetailTour, track, ti)"
                class="p-1 text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
                :disabled="mobileTrackDownloading.has(track.clientId ?? '')"
                title="Track options"
              >
                <MoreHorizontalIcon class="w-4 h-4" />
              </button>
            </div>
          </template>
          <p v-else class="text-[13px] text-gray-400 italic">None</p>
        </div>

        <!-- Waypoints -->
        <div>
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Waypoints ({{ mobileDetail?.waypoints.length ?? 0 }})
          </p>
          <template v-if="mobileDetail?.waypoints.length">
            <div
              v-for="(wp, wi) in mobileDetail.waypoints"
              :key="wp.clientId ?? wi"
              class="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0"
            >
              <span class="text-[13px] text-gray-400 shrink-0 mt-0.5">📍</span>
              <div class="flex-1 min-w-0">
                <p class="text-[13px] text-gray-700 leading-snug">
                  {{ wp.name || `Waypoint ${wi + 1}` }}
                </p>
                <p v-if="wp.description" class="text-[11px] text-gray-400 mt-0.5">
                  {{ wp.description }}
                </p>
              </div>
            </div>
          </template>
          <p v-else class="text-[13px] text-gray-400 italic">None</p>
        </div>

        <!-- Source file footer -->
        <div class="text-[12px] text-gray-400 pb-1">
          <template v-if="mobileSourceTour">
            Based on
            <a
              :href="mobileSourceTourHref"
              class="text-primary-500 hover:underline"
              @click="openMobileSourceTour"
              >{{ mobileSourceTour.name }}</a
            >
          </template>
          <template v-else-if="mobileSourceFileLabel">
            Based on
            <a
              :href="apiService.getTourSourceFileUrl(mobileTourDetailTour.clientId)"
              :download="mobileSourceFileLabel"
              class="text-primary-500 hover:underline"
              >{{ mobileSourceFileLabel }}</a
            >
          </template>
          <template v-else>
            <span class="italic">generated</span>
          </template>
        </div>
      </template>
    </div>

    <div
      v-if="selectedRouteActionBarVisible"
      class="relative z-20 shrink-0 border-t border-gray-200 bg-white/95 px-3 py-2 backdrop-blur"
      data-testid="selected-route-mobile-actions"
    >
      <div class="grid h-11 grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-2">
        <div class="grid min-w-0 grid-cols-2 gap-1.5">
          <button
            v-if="props.canShowSelectedRouteDetails"
            type="button"
            class="flex min-w-0 items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-2 text-sm font-medium text-gray-700 shadow-sm active:bg-gray-50"
            aria-label="Open route details"
            @click="emit('open-route-details')"
          >
            <InfoIcon class="h-4 w-4 shrink-0" />
            <span class="truncate">Details</span>
          </button>
          <div v-else></div>

          <button
            v-if="props.canShowSelectedRouteProfile"
            type="button"
            class="flex min-w-0 items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-2 text-sm font-medium text-gray-700 shadow-sm active:bg-gray-50"
            aria-label="Open route profile"
            @click="emit('open-route-profile')"
          >
            <GaugeIcon class="h-4 w-4 shrink-0" />
            <span class="truncate">Profile</span>
          </button>
        </div>
        <button
          v-if="props.canNavigateSelectedRoute && props.selectedRouteTourId"
          type="button"
          class="flex min-w-0 items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-3 text-sm font-semibold text-white shadow-sm active:bg-emerald-800"
          aria-label="Start navigation"
          @click="emit('navigate-tour', props.selectedRouteTourId)"
        >
          <NavigationIcon class="h-4 w-4 shrink-0" />
          <span class="truncate">Navigate</span>
        </button>
      </div>
    </div>

    <!-- Track action sheet -->
    <Teleport to="body">
      <template v-if="mobileTrackActionOpen && mobileTrackActionTrack">
        <div class="fixed inset-0 z-9999 flex items-end">
          <div class="absolute inset-0 bg-black/40" @click="closeMobileTrackActionSheet"></div>
          <div class="relative w-full bg-white rounded-t-2xl shadow-xl">
            <div class="flex justify-center pt-2.5 shrink-0">
              <div class="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>
            <div class="px-4 pt-3 pb-2 border-b border-gray-100">
              <p class="text-sm font-semibold text-gray-700 truncate">
                {{ mobileTrackActionTrack.name || `Track ${mobileTrackActionTi + 1}` }}
              </p>
            </div>
            <div class="py-1">
              <button
                @click="mobileDownloadTrack"
                class="flex w-full items-center gap-3 px-4 py-3 text-[15px] text-gray-700 hover:bg-gray-50 cursor-pointer"
              >
                <DownloadIcon class="w-4 h-4 text-gray-400 shrink-0" />
                Download GPX
              </button>
              <button
                @click="mobileDeleteTrack"
                class="flex w-full items-center gap-3 px-4 py-3 text-[15px] text-red-600 hover:bg-red-50 cursor-pointer"
              >
                <TrashIcon class="w-4 h-4 text-red-400 shrink-0" />
                Delete track
              </button>
            </div>
            <button
              @click="closeMobileTrackActionSheet"
              class="w-full py-3 text-[15px] text-gray-500 font-medium border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </template>
    </Teleport>

    <!-- Pinned workspace footer (portal target for WorkspaceSearchPanel note) -->
    <div id="workspace-collections-footer"></div>
  </div>

  <nav
    v-if="isMobileBatchMode"
    class="fixed left-1/2 max-w-[calc(100vw-1.5rem)] -translate-x-1/2 rounded-full bg-white shadow-lg"
    :style="{
      zIndex: Z_INDEX.MOBILE_TAB_BAR,
      bottom: mobileBatchActionBarBottom,
    }"
    aria-label="Selection actions"
  >
    <div class="flex h-16 items-center justify-center gap-1 px-2">
      <button
        v-for="action in mobileBatchActionBarItems"
        :key="action.key"
        type="button"
        :disabled="action.disabled"
        class="flex h-12 w-12 min-w-0 items-center justify-center rounded-full px-1 text-[11px] font-medium transition"
        :class="[
          action.disabled
            ? 'cursor-not-allowed text-gray-400 opacity-70'
            : 'hover:cursor-pointer hover:bg-gray-100',
          !action.disabled && action.tone === 'amber'
            ? 'text-amber-700'
            : !action.disabled && action.tone === 'danger'
              ? 'text-red-600'
              : !action.disabled
                ? 'text-gray-700'
                : '',
        ]"
        :aria-label="action.disabledReason ?? action.label"
        :title="action.disabledReason ?? action.label"
        @click="runMobileBatchAction(action)"
      >
        <component :is="action.icon" class="h-5 w-5" />
      </button>
    </div>
  </nav>

  <!-- Share Modal - Outside main toolbar to avoid v-show="!isDialogOpen" hiding it -->
  <ShareModal :is-open="isShareModalOpen" @close="closeShareModal" />

  <!-- Upload Dialog - Outside main toolbar to avoid v-show="!isDialogOpen" hiding it -->
  <UploadDialog
    :is-open="isUploadDialogOpen"
    :allow-photo-upload="allowPhotoUploadInDialog"
    @close="closeUploadDialog"
    @trigger-gpx-upload="handleUploadDialogGpx"
    @trigger-photo-upload="handleUploadDialogPhoto"
  />

  <CreateTourModal
    :is-open="isCreateTourModalOpen"
    mode="route"
    @close="isCreateTourModalOpen = false"
    @create="handleCreateRouteFromModal($event)"
  />

  <OfflineDownloadDialog
    v-if="isTauriApp"
    :is-open="isOfflineDialogOpen"
    :style-preference="currentStylePreference"
    @close="closeOfflineDialog"
  />

  <GpxDownloadOptionsModal
    :is-open="isWorkspaceGpxDownloadModalOpen"
    title="Download Collection GPX"
    download-label="Download Collection GPX"
    :is-downloading="isWorkspaceGpxDownloading"
    @close="closeWorkspaceGpxDownloadModal"
    @download="handleWorkspaceGpxDownload"
  />

  <Teleport to="body">
    <TourDeleteModal
      :open="isMobileBatchSingleDeleteOpen"
      :tour="mobileBatchSingleDeleteTour"
      :tour-name="
        mobileBatchSingleDeleteTour ? getEffectiveTourName(mobileBatchSingleDeleteTour) : null
      "
      :deleting="isMobileBatchSingleDeleting"
      :include-current-collection="includeCurrentCollectionInMobileBatchDelete"
      @close="closeMobileBatchSingleDelete"
      @confirm="confirmMobileBatchSingleDelete"
    />
  </Teleport>

  <MobileActionSheet
    :open="isWorkspaceInfoSheetOpen"
    title="Collection info"
    :preferred-snap="workspaceInfoSheetInitialSnap"
    :medium-height-px="workspaceInfoSheetExpandedHeight"
    :high-height-px="workspaceInfoSheetHighHeight"
    preserve-bottom-panel-state
    allow-high-swipe-through-dismiss
    @close="closeWorkspaceInfoSheet"
  >
    <template #header-left>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition hover:cursor-pointer hover:bg-gray-50 hover:text-gray-800"
        :disabled="isWorkspaceInfoNameSaving || isWorkspaceInfoDescriptionSaving"
        :title="isWorkspaceInfoNameEditing ? 'Cancel rename' : 'Close'"
        :aria-label="isWorkspaceInfoNameEditing ? 'Cancel rename' : 'Close collection info'"
        @click="handleWorkspaceInfoHeaderLeft"
      >
        <XIcon class="h-4 w-4" />
      </button>
    </template>

    <template #title>
      <BaseInput
        v-if="isWorkspaceInfoNameEditing"
        id="workspace-info-name"
        v-model="workspaceInfoNameDraft"
        class="min-w-0 !py-1.5"
        :invalid="!!workspaceInfoNameError"
        :disabled="isWorkspaceInfoNameSaving"
        maxlength="255"
        autocomplete="off"
        enterkeyhint="done"
        aria-label="New collection name"
        @input="workspaceInfoNameError = ''"
        @keydown.enter.prevent="saveWorkspaceInfoName"
        @keydown.esc.prevent="cancelWorkspaceInfoNameEdit"
      />
      <div v-else class="relative flex min-w-0 max-w-[calc(100%-2rem)] flex-col items-center">
        <div class="flex max-w-full min-w-0 items-baseline justify-center gap-1">
          <span
            class="min-w-0 truncate text-center text-base font-semibold text-gray-900"
            :title="selectedWorkspaceName"
          >
            {{ selectedWorkspaceName }}
          </span>
          <span
            v-if="workspaceAuthorByline"
            data-testid="workspace-info-author-byline"
            class="max-w-[45%] shrink truncate text-sm font-normal text-gray-500"
            :title="`by ${workspaceAuthorByline}`"
          >
            by {{ workspaceAuthorByline }}
          </span>
          <BaseIconButton
            v-if="canRenameWorkspaceInfo"
            size="sm"
            class="absolute left-full top-0 ml-1"
            :disabled="isWorkspaceInfoDescriptionSaving"
            title="Rename collection"
            aria-label="Rename collection"
            @click="startWorkspaceInfoNameEdit"
          >
            <PencilIcon class="h-3.5 w-3.5" />
          </BaseIconButton>
        </div>
        <p
          v-if="isWorkspaceInfoMembersLoading"
          data-testid="workspace-info-members-loading"
          class="mt-0.5 text-xs font-medium leading-tight text-gray-400"
        >
          Loading members…
        </p>
        <p
          v-else-if="showWorkspaceInfoMembers"
          data-testid="workspace-info-members"
          class="mt-0.5 max-w-full truncate text-xs font-medium leading-tight text-gray-500"
          :title="`With ${workspaceInfoMemberNames.join(', ')}`"
        >
          With {{ workspaceInfoMemberNames.join(', ') }}
        </p>
      </div>
    </template>

    <template #header-right>
      <button
        v-if="isWorkspaceInfoNameEditing"
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-white text-emerald-700 transition hover:cursor-pointer hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isWorkspaceInfoNameSaving || !workspaceInfoNameDraft.trim()"
        aria-label="Confirm rename"
        @click="saveWorkspaceInfoName"
      >
        <CheckIcon class="h-4 w-4" />
      </button>
    </template>

    <div class="flex shrink-0 flex-col gap-4 px-4 py-3">
      <p v-if="workspaceInfoNameError" class="text-sm text-danger-700" role="alert">
        {{ workspaceInfoNameError }}
      </p>

      <div class="shrink-0 overflow-hidden rounded-2xl">
        <img
          v-if="workspaceStore.coverPhotoUrl"
          :src="workspaceStore.coverPhotoUrl"
          alt=""
          class="h-44 w-full select-none object-cover"
          draggable="false"
          @contextmenu.prevent
        />
        <div
          v-else
          class="flex h-44 w-full items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-100 to-emerald-200"
        >
          <RouteIcon class="h-8 w-8 text-emerald-700/70" />
        </div>
      </div>

      <section v-if="workspaceInfoDateRange" class="shrink-0 px-1">
        <h2 class="text-sm font-semibold text-gray-900">Date</h2>
        <p class="mt-2 text-sm leading-6 text-gray-700">
          {{ workspaceInfoDateRange }}
        </p>
      </section>

      <section v-if="workspaceInfoStats.length" class="shrink-0 px-1">
        <h2 class="text-sm font-semibold text-gray-900">Stats</h2>
        <p class="mt-2 text-sm leading-6 text-gray-700 tabular-nums">
          {{ workspaceInfoStats.join(' · ') }}
        </p>
      </section>

      <section class="shrink-0 px-1 pb-2">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-sm font-semibold text-gray-900">Description</h2>
          <BaseButton
            v-if="canEditWorkspaceInfoDescription && !isWorkspaceInfoDescriptionEditing"
            variant="ghost"
            size="sm"
            class="!flex-none !px-0 !py-1 !font-bold text-primary-700 hover:!bg-transparent"
            :disabled="isWorkspaceInfoNameSaving"
            @click="startWorkspaceInfoDescriptionEdit"
          >
            Edit
          </BaseButton>
          <div v-else-if="isWorkspaceInfoDescriptionEditing" class="flex items-center gap-4">
            <BaseButton
              variant="ghost"
              size="sm"
              class="!flex-none !px-0 !py-1 text-gray-600 hover:!bg-transparent"
              :disabled="isWorkspaceInfoDescriptionSaving"
              @click="cancelWorkspaceInfoDescriptionEdit"
            >
              Cancel
            </BaseButton>
            <BaseButton
              variant="ghost"
              size="sm"
              class="!flex-none !px-0 !py-1 !font-bold text-primary-700 hover:!bg-transparent"
              :loading="isWorkspaceInfoDescriptionSaving"
              @click="saveWorkspaceInfoDescription"
            >
              Save
            </BaseButton>
          </div>
        </div>

        <template v-if="isWorkspaceInfoDescriptionEditing">
          <BaseInput
            id="workspace-info-description"
            v-model="workspaceInfoDescriptionDraft"
            multiline
            rows="4"
            maxlength="2048"
            class="mt-2 min-h-24 max-h-56 resize-none overflow-y-auto overscroll-contain leading-5"
            :invalid="!!workspaceInfoDescriptionError"
            :disabled="isWorkspaceInfoDescriptionSaving"
            placeholder="Add a description to this collection..."
            aria-label="Collection description"
            @input="resizeWorkspaceInfoDescription"
            @touchstart="handleWorkspaceInfoDescriptionTouchStart"
            @touchmove="handleWorkspaceInfoDescriptionTouchMove"
            @touchend="handleWorkspaceInfoDescriptionTouchEnd"
            @touchcancel="handleWorkspaceInfoDescriptionTouchEnd"
            @keydown.enter.exact.prevent="saveWorkspaceInfoDescription"
            @keydown.shift.enter.stop
            @keydown.esc.prevent="cancelWorkspaceInfoDescriptionEdit"
          />
          <p
            v-if="workspaceInfoDescriptionError"
            class="mt-1.5 text-sm text-danger-700"
            role="alert"
          >
            {{ workspaceInfoDescriptionError }}
          </p>
        </template>

        <template v-else>
          <p
            v-if="workspaceStore.description"
            class="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700"
          >
            {{ workspaceStore.description }}
          </p>
          <p v-else class="mt-2 text-sm italic leading-6 text-gray-500">No description.</p>
        </template>
      </section>
    </div>
  </MobileActionSheet>
</template>

<style scoped>
/* Ensure touch events work properly on mobile */
.mobile-toolbar {
  /* Force bottom positioning with high specificity. The bottom offset is driven by a CSS
     variable so the panel can float above the persistent MobileTabBar (set inline). */
  position: fixed !important;
  bottom: var(--mobile-panel-bottom, 0px) !important;
  top: auto !important;
  left: var(--mobile-panel-left, 0px) !important;
  right: var(--mobile-panel-right, 0px) !important;
  width: auto !important;
  /* Override any inherited positioning */
  transform: none !important;
  /* Force proper rendering */
  -webkit-transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  /* Ensure proper touch handling */
  touch-action: pan-y;
  /* Ensure it's not affected by parent containers */
  margin: 0 !important;
}

/* Touch-device specific fixes */
.is-touch {
  /* Fix for mobile browser position fixed issues */
  -webkit-overflow-scrolling: touch;
  /* Ensure proper rendering */
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
  /* Prevent accidental zoom/select on touch */
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Smooth animations */
.panel-transition {
  transition:
    transform 0.3s ease-out,
    height 0.3s ease-out;
}

/* Better iOS touch handling */
@supports (-webkit-touch-callout: none) {
  /* iOS Safari specific styles */
  .mobile-toolbar {
    /* Ensure fixed positioning works on iOS */
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
  }
}

/* Safari-specific smooth scrolling */
.scrollable-content {
  -webkit-overflow-scrolling: touch;
}

@keyframes bike-ride {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(calc(100vw - 2rem));
  }
}

.animate-bike-ride {
  animation: bike-ride 4s ease-in-out infinite;
}

/* Animation: Collections to Collection Detail (fade out right, fade in left) */
.slide-fade-left-enter-active,
.slide-fade-left-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-fade-left-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-fade-left-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Animation: Collection Detail to Collections (fade out left, fade in right) */
.slide-fade-right-enter-active,
.slide-fade-right-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-fade-right-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-fade-right-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
